import {
  Box,
  Grid,
  Typography,
  Avatar,
  Stack,
  Chip,
  Button,
  Paper,
  Divider,
  Modal,
  Skeleton,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Pagination,
  PaginationItem
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import { useNavigate, useLocation, useSearchParams, useParams } from "react-router-dom";
import ShareIcon from "@mui/icons-material/Share";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OceshaLogo from "../../../assets/ocesha_images/ocesha_logo.png";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import VerifiedIcon from "@mui/icons-material/Verified";
import { ReactComponent as VerifiedTick } from "../../../assets/podcast_monster/verified-tick.svg";
import MailOutlinedIcon from "@mui/icons-material/MailOutlined";
import EastOutlinedIcon from "@mui/icons-material/EastOutlined";
import PlayCircleOutlineOutlinedIcon from "@mui/icons-material/PlayCircleOutlineOutlined";
import { useGetUserDetailsMutation } from "redux/api/authApi";
import { getCookie } from "utils/storageUtils";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useGetGeneratedPublishEpisodeListingsMutation, useGetgeneratedBlogListingsQuery, useGetGeneratedPublishClipsListingsMutation, useGetGeneratedPublishImageListingsMutation } from "redux/api/promptApi";
import { isMobile } from "react-device-detect";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Header from "../../auth/Header";
import dummyBlogImg from "../../../assets/ocesha_images/dummy_blog_image.png";


/* ── Light theme tokens matching Creator Directory ── */
const interFont = "'Inter', sans-serif";
const cardBg = "#161b22";
const cardBorder = "#21262d";
const primary = "#3b82f6";
const primaryLight = "#60a5fa";

/* ── Inter font injection ── */
const interFontStyle = (
  <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        .instructor-profile-root, .instructor-profile-root * { font-family: 'Inter', sans-serif !important;
    }
    `}</style>
);

const SkillChip = ({ label }: { label: string }) => (
  <Chip
    label={label}
    size="small"
    sx={{
      background: "#0d1117",
      color: "#d1d5db",
      fontSize: 11,
      height: 22,
      borderRadius: "999px",
      border: "1px solid #30363d",
      fontFamily: "Inter, sans-serif",
    }}
  />
);

/* -------------------------------- COURSE CARD -------------------------------- */
const CourseCard = ({ course, selected, onSelect, return_domain, loggedIn }: any) => {
  const navigate = useNavigate();

  // Dynamic rating values (falls back to 0 when the API doesn't return them),
  // matching the pattern used in IndividualCoursesPage.tsx
  const ratingValue = course?.average_rating || 0;
  const reviewCount = course?.total_reviews ?? course?.review_count ?? 0;

  return (
    <Paper
      sx={{
        p: 1.5,
        borderRadius: "12px",
        background: cardBg,
        border: `1px solid ${cardBorder}`,
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        fontFamily: "Inter, sans-serif",
        transition: "box-shadow 0.2s ease, border-color 0.2s ease",
        "&:hover": {
          boxShadow: "0 4px 20px rgba(59,130,246,0.15)",
          borderColor: "#3b82f6",
        },
      }}
    >
      <Box sx={{ position: "relative" }}>
        {/* <Checkbox
          checked={selected}
          onChange={() => onSelect(course._id)}
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: "absolute",
            top: 8,
            left: 8,
            zIndex: 10,
            background: "rgba(0,0,0,0.6)",
            borderRadius: "50%",
            color: "#fff",
            "&.Mui-checked": {
              color: "#4BA5EB",
            },
          }}
        /> */}
        <Box
          component="img"
          src={course.image_path_small || (dummyBlogImg as string)}
          onError={(e: any) => {
            e.target.src = dummyBlogImg as string;
          }}
          sx={{
            width: "100%",
            height: 232,
            objectFit: "cover",
            borderRadius: "16px",
            cursor: "pointer",
            maxWidth: '100%'
          }}
          alt={course.title}
          onClick={() =>
            navigate("/codegenie/course-detail", {
              state: { courseId: course._id },
            })
          }
        />
        {course.bestseller && (
          <Chip
            label="BESTSELLER"
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              height: 20,
              fontSize: 10,
              background: "#6b7280",
              color: "#fff",
              fontFamily: "Inter, sans-serif",
            }}
          />
        )}
      </Box>

      <Typography
        mt={1.5}
        fontSize={13}
        fontWeight={600}
        color="#e5e7eb"
        sx={{
          cursor: "pointer",
          fontFamily: "Inter, sans-serif",
          lineHeight: 1.4,
          flexGrow: 1,
        }}
        onClick={() =>
          navigate("/codegenie/course-detail", {
            state: { courseId: course._id },
          })
        }
      >
        {course.title}
      </Typography>

      {/* UI: flexWrap + gap on tags row, matches Myprofilepage */}
      <Stack direction="row" spacing={0.5} mt={1} flexWrap="wrap" gap={0.5}>
        {course?.tags?.map((t: string) => (
          <Chip
            key={t}
            label={t}
            size="small"
            sx={{
              height: 18,
              fontSize: 10,
              border: "1px solid #30363d",
              background: "#0d1117",
              color: "#9ca3af",
              borderRadius: "4px",
              fontFamily: "Inter, sans-serif",
            }}
          />
        ))}
      </Stack>

      {/* Rating: dynamic, sourced from course.average_rating / course.total_reviews
          (same fields used in IndividualCoursesPage.tsx) instead of hardcoded values */}
      <Stack direction="row" spacing={1} alignItems="center" mt={1}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            px: 0.8,
            py: 0.2,
            border: "1px solid #30363d",
            borderRadius: "6px",
            background: "#0d1117",
          }}
        >
          {[1, 2, 3, 4, 5].map((i) => (
            <StarIcon
              key={i}
              sx={{
                fontSize: 14,
                color: i <= Math.round(ratingValue) ? "#facc15" : "#374151",
              }}
            />
          ))}
          <Typography
            fontSize="13px"
            color="#e5e7eb"
            ml={0.3}
            fontFamily="Inter, sans-serif"
          >
            {ratingValue}
          </Typography>
        </Box>
        <Box
          sx={{
            px: 0.8,
            py: 0.2,
            border: "1px solid #30363d",
            borderRadius: "6px",
            background: "#0d1117",
          }}
        >
          <Typography
            fontSize="11px"
            color="#9ca3af"
            fontFamily="Inter, sans-serif"
          >
            {reviewCount} ratings
          </Typography>
        </Box>
      </Stack>

      {/* UI: price row — strikethrough $399 + View Course button */}
      <Stack direction="row" spacing={1} mt={1} alignItems="center">
        <Typography
          color="#e5e7eb"
          fontWeight={700}
          fontSize={14}
          fontFamily="Inter, sans-serif"
        >
          ${course.suggested_price + " USD"}
        </Typography>
        <Typography
          fontSize={12}
          color="#6b7280"
          fontFamily="Inter, sans-serif"
          sx={{ textDecoration: "line-through" }}
        >
          $399
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{ ml: "auto", cursor: "pointer" }}
          onClick={() => {
            if (course.public_url) {
              window.open(course.public_url, "_blank");
            }
            else {
              localStorage.setItem("selected_course_id", course._id);
              navigate("/codegenie/individual-course", {
                state: { courseId: course._id },
              });
            }


            //  navigate(`${course.public_url}`, {
            //       state: { courseId: course._id },
            //     })
          }
          }
        >
          <Typography
            fontSize={12}
            color="#3b82f6"
            fontWeight={600}
            fontFamily="Inter, sans-serif"
          >
            View Course
          </Typography>
          <EastOutlinedIcon sx={{ color: "#3b82f6", fontSize: 16 }} />
        </Stack>
      </Stack>
    </Paper>
  );
};

const CourseCardSkeleton = () => (
  <Paper
    sx={{
      p: 1.5,
      borderRadius: "12px",
      background: cardBg,
      border: `1px solid ${cardBorder}`,
      height: "100%",
      width: "100%",
      display: "flex",
      flexDirection: "column",
    }}
  >
    <Skeleton
      variant="rectangular"
      height={232}
      sx={{ borderRadius: "16px" }}
    />
    <Skeleton variant="text" height={28} sx={{ mt: 1 }} />
    <Stack direction="row" spacing={1} mt={1}>
      <Skeleton variant="rounded" width={60} height={18} />
      <Skeleton variant="rounded" width={60} height={18} />
    </Stack>
    <Stack direction="row" spacing={1} mt={1}>
      <Skeleton variant="rounded" width={100} height={24} />
      <Skeleton variant="rounded" width={80} height={24} />
    </Stack>
    <Skeleton variant="text" width={120} sx={{ mt: 1 }} />
  </Paper>
);

/* ── Dummy placeholder card: full-size image only, no text ── */
/* ── Dummy placeholder card: matches full CourseCard / BlogCard size ── */
const DummyImageCard = ({ variant = "course" }: { variant?: "course" | "blog" }) => (
  <Paper
    sx={{
      p: 1.5,
      borderRadius: "12px",
      background: cardBg,
      border: `1px solid ${cardBorder}`,
      width: "100%",
      height: "100%",
    }}
  >
    <Box
      component="img"
      src={dummyBlogImg as string}
      sx={{
        width: "100%",
        height: variant === "blog" ? 160 : 232,
        objectFit: "cover",
        borderRadius: variant === "blog" ? "10px" : "16px",
        display: "block",
        opacity: 0.4,
        maxwidth: '100%'
      }}
      alt=""
    />
    <Box sx={{ mt: 1.5 }}>
      <Skeleton variant="text" height={20} width="85%" sx={{ bgcolor: "#21262d" }} />
      <Skeleton variant="text" height={16} width="60%" sx={{ bgcolor: "#21262d", mt: 0.5 }} />
    </Box>
    <Stack direction="row" spacing={0.5} mt={1}>
      <Skeleton variant="rounded" width={50} height={18} sx={{ bgcolor: "#21262d" }} />
      <Skeleton variant="rounded" width={50} height={18} sx={{ bgcolor: "#21262d" }} />
    </Stack>
    <Stack direction="row" spacing={1} mt={1}>
      <Skeleton variant="rounded" width={100} height={24} sx={{ bgcolor: "#21262d" }} />
      <Skeleton variant="rounded" width={70} height={24} sx={{ bgcolor: "#21262d" }} />
    </Stack>
  </Paper>
);

export interface YouTubeEmbedOptions {
  autoplay?: boolean;
  rel?: boolean;
  modestbranding?: boolean;
  start?: number;
  end?: number;
  cc?: boolean;
}

export function toYouTubeEmbed(
  url: string | null,
  opts: YouTubeEmbedOptions = {},
): string | null {
  if (!url) return null;
  let id: string | null = null;
  try {
    const u = new URL(url.trim());
    if (u.hostname.includes("youtu.be")) {
      id = u.pathname.slice(1) || null;
    }
    if (
      !id &&
      (u.hostname.includes("youtube.com") ||
        u.hostname.includes("www.youtube.com"))
    ) {
      if (u.searchParams.has("v")) {
        id = u.searchParams.get("v");
      } else {
        const embedMatch = u.pathname.match(/\/embed\/([^/?]+)/);
        const shortsMatch = u.pathname.match(/\/shorts\/([^/?]+)/);
        if (embedMatch) id = embedMatch[1];
        else if (shortsMatch) id = shortsMatch[1];
      }
    }
    if (!id) return null;
    const params = new URLSearchParams();
    if (opts.autoplay) params.set("autoplay", "1");
    if (opts.rel === false) params.set("rel", "0");
    else if (opts.rel === true) params.set("rel", "1");
    if (opts.modestbranding) params.set("modestbranding", "1");
    if (typeof opts.start === "number" && opts.start > 0)
      params.set("start", String(Math.floor(opts.start)));
    if (typeof opts.end === "number" && opts.end > 0)
      params.set("end", String(Math.floor(opts.end)));
    if (opts.cc) params.set("cc_load_policy", "1");
    const query = params.toString();
    return `https://www.youtube.com/embed/${encodeURIComponent(id)}${query ? `?${query}` : ""}`;
  } catch (_err) {
    return null;
  }
}

/* -------------------------------- DUMMY COURSES -------------------------------- */
const DUMMY_COURSES = [
  { _id: "dummy_1", title: "", image_path_small: dummyBlogImg, tags: [], suggested_price: null, bestseller: false },
  { _id: "dummy_2", title: "", image_path_small: dummyBlogImg, tags: [], suggested_price: null, bestseller: false },
  { _id: "dummy_3", title: "", image_path_small: dummyBlogImg, tags: [], suggested_price: null, bestseller: false },
];

/* -------------------------------- DUMMY PODCASTS -------------------------------- */
const DUMMY_PODCASTS = [
  { _id: "dp_1", title: "", description: "", thump_image_path: dummyBlogImg },
  { _id: "dp_2", title: "", description: "", thump_image_path: dummyBlogImg },
  { _id: "dp_3", title: "", description: "", thump_image_path: dummyBlogImg },
  { _id: "dp_4", title: "", description: "", thump_image_path: dummyBlogImg },
];

/* -------------------------------- DUMMY BLOGS -------------------------------- */
const DUMMY_BLOGS = [
  { _id: "db_1", blog_title: "", blog_summary: "", blog_image_path: dummyBlogImg, created_at: "", tags: [] },
  { _id: "db_2", blog_title: "", blog_summary: "", blog_image_path: dummyBlogImg, created_at: "", tags: [] },
  { _id: "db_3", blog_title: "", blog_summary: "", blog_image_path: dummyBlogImg, created_at: "", tags: [] },
  { _id: "db_4", blog_title: "", blog_summary: "", blog_image_path: dummyBlogImg, created_at: "", tags: [] },
];

/* -------------------------------- DUMMY SOCIAL POSTS -------------------------------- */
const DUMMY_SOCIAL_POSTS = [
  { _id: "dummy_sp1", content: "", image_url: dummyBlogImg, platform: "twitter", created_at: "" },
  { _id: "dummy_sp2", content: "", image_url: dummyBlogImg, platform: "linkedin", created_at: "" },
  { _id: "dummy_sp3", content: "", image_url: dummyBlogImg, platform: "facebook", created_at: "" },
  { _id: "dummy_sp4", content: "", image_url: dummyBlogImg, platform: "twitter", created_at: "" },
];

/* -------------------------------- DUMMY IMAGES -------------------------------- */
const DUMMY_IMAGES = [
  { _id: "dummy_img1", image_path: dummyBlogImg, title: "" },
  { _id: "dummy_img2", image_path: dummyBlogImg, title: "" },
  { _id: "dummy_img3", image_path: dummyBlogImg, title: "" },
  { _id: "dummy_img4", image_path: dummyBlogImg, title: "" },
];

/* -------------------------------- PAGE -------------------------------- */
export default function InstructorProfilePage() {
  const navigate = useNavigate();
  const supportUserid = process.env.REACT_APP_SUPPORT_USER_ID || "";
  const loggedIn = localStorage.getItem("logged_in");
  // `activeItem` used to come from localStorage only, but localStorage is
  // shared across every tab on this origin. When this page is opened in a
  // NEW tab from the dashboard (via window.open, which bypasses the
  // dashboard's handleItemClick that writes to localStorage), localStorage
  // may hold a stale value from a different tab entirely. The dashboard
  // link always appends `?activeItem=profile` to the URL it opens, so read
  // that first — it's authoritative for *this* tab — and fall back to
  // localStorage only for the in-app (same-tab) navigation case.
  const activeItem =
    new URLSearchParams(window.location.search).get("activeItem") ||
    localStorage.getItem("activeItem");
  const isPublicProfile = window.location.pathname.includes("/profile/");
  const [userDetails, setUserDetails] = useState<any>("");
  const [showFullBio, setShowFullBio] = useState(false);
  const [courses, setCourses] = useState<any[]>([]);
  const [loadingCourses, setLoadingCourses] = useState<boolean>(false);
  // const [showAllCourses, setShowAllCourses] = useState(false);

  const [coursePage, setCoursePage] = useState<number>(1);
  const [courseLimit] = useState<number>(12);
  const [courseTotalPages, setCourseTotalPages] = useState<number>(1);
   const coursesSectionRef = useRef<HTMLDivElement>(null);
  const [courseTotal, setCourseTotal] = useState<number>(0);

  const [generatedEpisodes, setGeneratedEpisodes] = useState<any[]>([]);
  const [loadingEpisodes, setLoadingEpisodes] = useState<boolean>(false);
  const [showAllEpisodes, setShowAllEpisodes] = useState(false);
  const [getGeneratedEpisodeListingsMutation] =
    useGetGeneratedPublishEpisodeListingsMutation();

  // State for clips per episode
  const [episodesWithClips, setEpisodesWithClips] = useState<any[]>([]);
  const [loadingClipsForEpisode, setLoadingClipsForEpisode] = useState<{ [key: string]: boolean }>({});
  const [getGeneratedClipsListings] = useGetGeneratedPublishClipsListingsMutation();
  const [getGeneratedImageListings] = useGetGeneratedPublishImageListingsMutation();

  const [clipPageIndex, setClipPageIndex] = useState<{ [key: string]: number }>({});

  // State for Social Posts
  const [socialPosts, setSocialPosts] = useState<any[]>([]);
  const [loadingSocialPosts, setLoadingSocialPosts] = useState<boolean>(false);
  const [showAllSocialPosts, setShowAllSocialPosts] = useState(false);
  const [socialPostsPageIndex, setSocialPostsPageIndex] = useState<number>(0);
  const [selectedSocialPost, setSelectedSocialPost] = useState<any>(null);

  // State for Images
  const [images, setImages] = useState<any[]>([]);
  const [loadingImages, setLoadingImages] = useState<boolean>(false);
  const [showAllImages, setShowAllImages] = useState(false);
  const [imagesPageIndex, setImagesPageIndex] = useState<number>(0);

  const [helperPopup, setHelperPopup] = useState<boolean>(false);
  const [embedSrc, setEmbedSrc] = useState("");
  const [helpervideosrc, setHelperVideoSrc] = useState<any>();
  const [openMenuId, setOpenMenuId] = useState(null);
  const [userLoading, setUserLoading] = useState<boolean>(false);
  const location = useLocation();
  const [totalReviews, setTotalReviews] = useState(0);
  const [getUserDetailsMutation] = useGetUserDetailsMutation();
  const [isFollowing, setIsFollowing] = useState(false);
  const [loadingFollow, setLoadingFollow] = useState(false);
  const [openEnquiryModal, setOpenEnquiryModal] = useState(false);
  const [sendingEnquiry, setSendingEnquiry] = useState(false);
  const [imgError, setImgError] = useState(false);
  let user_email = localStorage.getItem("user_email") || getCookie("user_email") || "";

  const [enquiryData, setEnquiryData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });

  const [openImageViewer, setOpenImageViewer] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<{ src: string; title: string } | null>(null);

  /*  unpublishing feature */
  // unpublish courses
  const [selectedCourses, setSelectedCourses] = useState<string[]>([]);
  const [unpublishingCourses, setUnpublishingCourses] = useState(false);

  // unpublish images
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [unpublishingImages, setUnpublishingImages] = useState(false);

  // unpublish social posts
  const [selectedSocialPosts, setSelectedSocialPosts] = useState<string[]>([]);
  const [socialSelectAll, setSocialSelectAll] = useState(false);
  const [unpublishLoading, setUnpublishLoading] = useState(false);

  // unpublish blogs
  const [selectedBlogs, setSelectedBlogs] = useState<string[]>([]);
  const [blogsSelectAll, setBlogsSelectAll] = useState(false);
  const [unpublishBlogsLoading, setUnpublishBlogsLoading] = useState(false);

  // unpublish podcasts
  const [selectedPodcasts, setSelectedPodcasts] = useState<string[]>([]);
  const [podcastSelectAll, setPodcastSelectAll] = useState(false);
  const [unpublishPodcastLoading, setUnpublishPodcastLoading] = useState(false);

  // unpublish clips
  const [selectedClips, setSelectedClips] = useState<string[]>([]);
  const [clipsSelectAll, setClipsSelectAll] = useState(false);
  const [unpublishClipsLoading, setUnpublishClipsLoading] = useState(false);
  /*  */

  // ── Shareable URL support ──────────────────────────────────────────
  const [searchParams] = useSearchParams();
  const mentorIdFromUrl = searchParams.get("mentor_id");
  const [resolvedCreator, setResolvedCreator] = useState<any>(null);
  const [loadingProfile, setLoadingProfile] = useState<boolean>(false);
  const { mentorId: mentorIdParam } = useParams();
  // Resolve from path param (:mentorId) OR query param (?mentor_id=)
  const mentorId = mentorIdParam || mentorIdFromUrl || undefined;


  const return_domain = process.env.REACT_APP_RETURN_DOMAIN_NAME || "";

  const S = {
    previewCard: { background: "#15151D", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 14, overflow: "hidden" as const },
    previewHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px" },
    previewAvatar: {
      width: 36, height: 36, borderRadius: "50%",
      background: "linear-gradient(90deg, #0168B5 0%, #4BA5EB 100%)",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontWeight: 700, fontSize: 14, color: "#fff", flexShrink: 0,
    },
    videoThumb: {
      position: "relative" as const, background: "#0A0A12",
      aspectRatio: "16/9",
      display: "flex", alignItems: "center", justifyContent: "center",
      cursor: "pointer", overflow: "hidden" as const,
    },
  };

  useEffect(() => {
    if (!mentorId) {
      // No mentorId in the URL at all — nothing to resolve, unblock immediately
      setProfileResolved(true);
      return;
    }

    const fetchPublicProfile = async () => {
      setLoadingProfile(true);
      try {
        const decoded = decodeURIComponent(mentorId);
        const isMongoId = /^[a-f0-9]{24}$/i.test(decoded);

        let userData: any = {};

        if (isMongoId) {
          try {
            const r1 = await axios.post(
              `${apiUrl}/api/auth/getUserDetailByID`,
              null,
              { params: { id: decoded } }
            );
            const raw = r1.data;
            userData =
              raw?.data ?? raw?.user ?? raw?.result ??
              (Array.isArray(raw) ? raw[0] : null) ??
              (typeof raw === "object" && raw?.status !== undefined
                ? Object.fromEntries(Object.entries(raw).filter(([k]) => k !== "status" && k !== "message"))
                : raw) ?? {};
          } catch (e) {
            console.log("fetchPublicProfile getUserDetailByID failed =>", e);
          }
        } else {
          try {
            const r1 = await axios.post(
              `${apiUrl}/api/auth/getUserDetailByEmailID`,
              null,
              { params: { email: decoded, filter: "", start_date: "", end_date: "" } }
            );
            const raw = r1.data;
            userData =
              raw?.data ?? raw?.user ?? raw?.result ??
              (Array.isArray(raw) ? raw[0] : null) ??
              (typeof raw === "object" && raw?.status !== undefined
                ? Object.fromEntries(Object.entries(raw).filter(([k]) => k !== "status" && k !== "message"))
                : raw) ?? {};
          } catch (e) {
            console.log("fetchPublicProfile getUserDetailByEmailID failed =>", e);
          }
        }

        if (!userData?._id && !userData?.email) {
          try {
            if (isMongoId) {
              const r2 = await axios.post(
                `${apiUrl}/api/auth/getUserDetailByEmailID`,
                null,
                { params: { email: decoded, filter: "", start_date: "", end_date: "" } }
              );
              const raw = r2.data;
              userData = raw?.data ?? raw?.user ?? raw?.result ?? (Array.isArray(raw) ? raw[0] : raw) ?? {};
            } else {
              const r2 = await axios.post(
                `${apiUrl}/api/auth/getUserDetailByID`,
                null,
                { params: { id: decoded } }
              );
              const raw = r2.data;
              userData = raw?.data ?? raw?.user ?? raw?.result ?? (Array.isArray(raw) ? raw[0] : raw) ?? {};
            }
          } catch (e) {
            console.log("fetchPublicProfile fallback failed =>", e);
          }
        }

        user_email = userData.email;

        let skills: string[] = [];
        const rawSkills1 = userData.skills;
        if (Array.isArray(rawSkills1)) {
          skills = rawSkills1.filter(Boolean);
        } else if (typeof rawSkills1 === "string" && rawSkills1.trim()) {
          skills = rawSkills1.split(",").map((s: string) => s.trim()).filter(Boolean);
        }

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "profile_identified",
          profile_slug: mentorId,     // e.g. "rohan-7523"
          user_email: userData.email || userData.user_email || "",       // whoever you're tracking
          page: "profile",
        });


        setResolvedCreator({
          ...userData,
          user_email: userData.email || userData.user_email || "",
          email: userData.email || userData.user_email || "",
          name: userData.name || userData.first_name,
          profile_image: userData.user_image || userData.profile_image || userData.avatar || "",
          bio: userData.bio || userData.experience_summary || "",
          skills,
          total_learners: userData.total_learners ?? userData.learners_count ?? 0,
          mentor_id: userData._id,
          userid: userData._id,
        });
      } catch (err) {
        console.log("Profile fetch error:", err);
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchPublicProfile();
  }, [mentorId]);



  const stateCreator = location.state?.creator;
  const normalizedStateCreator = stateCreator
    ? {
      ...stateCreator,
      mentor_id: stateCreator.mentor_id || stateCreator.user_id || stateCreator._id || stateCreator.id,
      userid: stateCreator.userid || stateCreator.user_id || stateCreator._id || stateCreator.id,
      _id: stateCreator._id || stateCreator.user_id || stateCreator.mentor_id,
    }
    : null;
  const effectiveCreator = resolvedCreator || normalizedStateCreator;
  const useremail = effectiveCreator?.user_email || effectiveCreator?.email || user_email || "";
  const userid = effectiveCreator?.userid || effectiveCreator?._id || effectiveCreator?.mentor_id || "";
  // ──────────────────────────────────────────────────────────────────

  const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";

  const [blogs, setBlogs] = useState<any[]>([]);
  const [loadingBlogs, setLoadingBlogs] = useState<boolean>(false);
  const [showAllBlogs, setShowAllBlogs] = useState(false);
  const [profileResolved, setProfileResolved] = useState<boolean>(false);


  // Use same RTK query as blogs_section — fetches from get_published_and_unpublished endpoint
  const getBlogsRTK = useGetgeneratedBlogListingsQuery(
    { promptid: "", userid: useremail, limitval: 50, skipval: 0 },
    { skip: !profileResolved || !useremail }
  );


  // Sync RTK query result into blogs state
  /* useEffect(() => {
    if (getBlogsRTK?.data && Array.isArray(getBlogsRTK.data)) {
      setBlogs(getBlogsRTK.data);
    }
    setLoadingBlogs(getBlogsRTK?.isLoading || getBlogsRTK?.isFetching || false);
  }, [getBlogsRTK?.data, getBlogsRTK?.isLoading, getBlogsRTK?.isFetching]); */
  const fetchBlogs = async () => {
    // if (!useremail) return;
    const useremail = effectiveCreator?.user_email || effectiveCreator?.email || "";
    if (!useremail) return;

    try {
      setLoadingBlogs(true);

      const promptid = "";
      const userid = useremail;
      const limitval = 50;
      const skipval = 0;

      const response = await fetch(
        `${apiUrl}/api/prompts/listBlogsByUserID_published?promptid=${promptid}&userid=${userid}&limitval=${limitval}&skipval=${skipval}`
      );


      const data = await response.json();


      // if (Array.isArray(data.data)) {
      console.log('published blogs listing res==>', data.data);
      setBlogs(data.data);
      /* } else {
        setBlogs([]);
      } */
    } catch (error) {
      console.error("Error fetching blogs:", error);
      setBlogs([]);
    } finally {
      setLoadingBlogs(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, [!loadingProfile, profileResolved, useremail]);

  const fetchReviews = async () => {
    const mentorId =
      effectiveCreator?.mentor_id || effectiveCreator?._id || effectiveCreator?.userid;

    if (!mentorId) return;

    try {
      const res = await axios.get(
        `${apiUrl}/api/student/reviews/mentor/${mentorId}`,
      );
      setTotalReviews(res.data?.total_reviews || 0);
    } catch (err) {
      console.log("Review fetch error", err);
    }
  };

  /* useEffect(() => {
    if (effectiveCreator) {
      fetchReviews();
    }
  }, [!loadingProfile, profileResolved, effectiveCreator]); */

  const getCourselisting = async (page: number = 1) => {
    if (!useremail) return;

    setLoadingCourses(true);
    try {
      const res = await axios.get(
        // `${apiUrl}/api/prompts/courses/by-user?user_email=${useremail}`,
        `${apiUrl}/api/prompts/courses/by-user-published`,
        {
          params: {
            user_email: useremail || user_email,
            page,
            limit: courseLimit,
          },
        }
      );
      setCourses(res?.data?.courses || []);
      setCourseTotal(res?.data?.pagination?.total || 0);

      setCourseTotalPages(
        res?.data?.pagination?.total_pages || 1
      );

      // Keep page in sync with API response
      setCoursePage(
        res?.data?.pagination?.page || page
      );
    } catch (error: any) {
      console.log("Error fetching courses listing=>", error);
      setCourses([]);
      setCourseTotal(0);
      setCourseTotalPages(1);
    } finally {
      setLoadingCourses(false);
    }
  };

  const handleCoursePageChange = async (
    _event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    if (page === coursePage) return;
    setCoursePage(page);
    await getCourselisting(page);
    // Wait for the new cards to render, then bring the Courses heading into view
    requestAnimationFrame(() => {
      coursesSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  // Fetch clips for a specific episode
  const fetchClipsForEpisode = async (episode: any) => {
    const productId = episode?.campaign_id || episode?._id || "";
    const userEmail = episode?.user_email || useremail;

    if (!userEmail || !productId) return [];

    try {
      setLoadingClipsForEpisode(prev => ({ ...prev, [productId]: true }));
      const response = await getGeneratedClipsListings({
        email: userEmail,
        product_id: productId,
        limitval: 100,
        skipval: 0,
        search: "",
      });
      if ("data" in response && Array.isArray(response.data)) {
        return response.data;
      }
      return [];
    } catch (_) {
      return [];
    } finally {
      setLoadingClipsForEpisode(prev => ({ ...prev, [productId]: false }));
    }
  };

  // Fetch episodes and then fetch clips for each episode
  const fetchEpisodeData = async () => {
    try {
      setLoadingEpisodes(true);
      const response = await getGeneratedEpisodeListingsMutation({
        email: useremail,
        limitval: 800,
        skipval: 0,
        search: "",
        fromDate: undefined,
        toDate: undefined,
      });
      if ("data" in response) {
        const { data } = response;
        setGeneratedEpisodes(data);

        // Fetch clips for each episode
        const episodesWithClipsData = await Promise.all(
          (data || []).map(async (episode: any) => {
            const clips = await fetchClipsForEpisode(episode);
            console.log("episodes......");
            console.log(episode);
            return {
              ...episode,
              clips: clips || []
            };
          })
        );
        setEpisodesWithClips(episodesWithClipsData);
      } else if ("error" in response) {
        const { error } = response;
        console.log("Error fetching episodes=>", error);
      }
    } catch (error: any) {
      console.log("Error fetching episodes=>", error);
    } finally {
      setLoadingEpisodes(false);
    }
  };

  // Fetch social posts for this instructor
  const fetchSocialPosts = async () => {
    if (!useremail) return;
    setLoadingSocialPosts(true);
    try {
      const response = await axios.get(`${apiUrl}/api/socialmedia/api/posts_published`/* `${apiUrl}/api/socialmedia/api/posts` */, {
        params: { user_email: useremail || user_email, limit: 50, page: 1 },
        withCredentials: true
      });
      setSocialPosts(response.data?.data || []);
    } catch (error) {
      console.log("Error fetching social posts", error);
    } finally {
      setLoadingSocialPosts(false);
    }
  };

  // Fetch images for this instructor using the same mutation as image_listing page
  const fetchImages = async () => {
    if (!useremail) return;
    setLoadingImages(true);
    try {
      const podcastId = localStorage.getItem("podcastId") || "";
      const response = await getGeneratedImageListings({
        email: useremail,
        product_id: podcastId,
        limitval: 50,
        skipval: 0,
      });
      if ("data" in response && Array.isArray(response.data)) {
        setImages(response.data);
      } else {
        setImages([]);
      }
    } catch (error) {
      console.log("Error fetching images", error);
      setImages([]);
    } finally {
      setLoadingImages(false);
    }
  };

  const email = effectiveCreator?.user_email || effectiveCreator?.email || "";

  useEffect(() => {
    // const email = effectiveCreator?.user_email || effectiveCreator?.email || "";
    if (email) {
      user_email = email;
      getCourselisting();
      fetchEpisodeData();
      fetchSocialPosts();
      fetchImages();
    }
  }, [!loadingProfile, profileResolved, email, user_email/* effectiveCreator */]);

  const bio = effectiveCreator?.bio || "";
  console.log('about me bio res=>', bio);
  const minChars = 550;
  const displayedBio = showFullBio ? bio : bio.slice(0, minChars);
  // const visibleCourses = showAllCourses ? courses : courses?.slice(0, 3);
  const visibleEpisodes = showAllEpisodes ? episodesWithClips : episodesWithClips?.slice(0, 3);

  const handleVideoClick = (url: string) => {
    const tmpurl = toYouTubeEmbed(url);
    setEmbedSrc(tmpurl ?? "");
    setHelperVideoSrc(url);
    setHelperPopup(true);
  };

  const handleClose = () => {
    setOpenMenuId(null);
  };

  const handleFollowToggle = async () => {
    const token = getCookie("token");

    const currentUserId = localStorage.getItem("userid");
    const targetUserId =
      effectiveCreator?.mentor_id ||
      effectiveCreator?.userid ||
      localStorage.getItem("mentor_id");

    if (!currentUserId || !targetUserId) {
      toast.error("User data missing");
      return;
    }

    try {
      setLoadingFollow(true);

      const url = isFollowing
        ? `${apiUrl}/api/auth/unfollow`
        : `${apiUrl}/api/auth/follow`;

      const res = await axios.post(
        url,
        {
          follower_id: currentUserId,
          following_id: targetUserId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setIsFollowing((prev) => !prev);

      toast.success(res.data.message || "Success");

    } catch (error: any) {
      console.log("ERROR =>", error?.response || error);

      toast.error(
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        "Follow failed"
      );
    } finally {
      setLoadingFollow(false);
    }
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setEnquiryData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClearEnquiry = () => {
    setEnquiryData({
      name: "",
      email: "",
      phone: "",
      subject: "",
      message: ""
    });
  };

  const handleSendEnquiry = async () => {
    if (!validateEnquiry()) return;
    const token = getCookie("token");

    const instructorId =
      effectiveCreator?.mentor_id ||
      effectiveCreator?.userid ||
      effectiveCreator?._id;

    if (!instructorId) return;

    try {
      setSendingEnquiry(true);

      await axios.post(
        `${apiUrl}/api/auth/sendEnquiry`,
        {
          instructorid: instructorId,
          ...enquiryData
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      toast.success("Message sent successfully ");
      setOpenEnquiryModal(false);

      // reset form
      setEnquiryData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: ""
      });

    } catch (error) {
      console.log("Enquiry error", error);
      toast.error("Failed to send message");
    } finally {
      setSendingEnquiry(false);
    }
  };

  const checkFollowStatus = async () => {
    const currentUserId = localStorage.getItem("userid");

    const targetUserId =
      effectiveCreator?.mentor_id ||
      effectiveCreator?.userid ||
      effectiveCreator?._id ||
      localStorage.getItem("mentor_id");

    if (!currentUserId || !targetUserId) return;

    try {
      const res = await axios.post(
        `${apiUrl}/api/auth/is-following`,
        {
          follower_id: currentUserId,
          following_id: targetUserId,
        }
      );

      console.log("FOLLOW STATUS =>", res.data);

      setIsFollowing(res.data.is_following);

    } catch (error) {
      console.log("Follow status error", error);
    }
  };

  /* useEffect(() => {
    if (effectiveCreator) {
      checkFollowStatus();
    }
  }, [!loadingProfile, profileResolved, effectiveCreator]); */

  const validateEnquiry = () => {
    const { name, email, phone, subject, message } = enquiryData;

    if (!name.trim()) {
      toast.error("Name is required");
      return false;
    }

    if (!email.trim()) {
      toast.error("Email is required");
      return false;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      toast.error("Invalid email format");
      return false;
    }

    if (!phone.trim()) {
      toast.error("Phone number is required");
      return false;
    }

    if (!subject.trim()) {
      toast.error("Subject is required");
      return false;
    }

    if (!message.trim()) {
      toast.error("Message is required");
      return false;
    }

    return true;
  };

  // const fetchData = async () => {
  //   const stateCreator = location.state?.creator;
  //   const emailOrId = mentorId || stateCreator?.user_email || stateCreator?.email;
  //   if (!emailOrId) return;

  //   try {
  //     let userData: any = {};
  //     const storedId = localStorage.getItem("mentor_id") || "";

  //     if (storedId) {
  //       try {
  //         const r1 = await axios.post(
  //           `${apiUrl}/api/auth/getUserDetailByID`,
  //           null,
  //           { params: { id: storedId } }
  //         );
  //         console.log("fetchData getUserDetailByID raw =>", r1.data);
  //         const raw = r1.data;
  //         userData =
  //           raw?.data ?? raw?.user ?? raw?.result ??
  //           (Array.isArray(raw) ? raw[0] : null) ??
  //           (typeof raw === "object" && raw?.status !== undefined
  //             ? Object.fromEntries(Object.entries(raw).filter(([k]) => k !== "status" && k !== "message"))
  //             : raw) ?? {};
  //       } catch (e) {
  //         console.log("getUserDetailByID failed =>", e);
  //       }
  //     }

  //     if (!userData._id && emailOrId) {
  //       try {
  //         const r2 = await axios.post(
  //           `${apiUrl}/api/auth/getUserDetailByEmailID`,
  //           null,
  //           { params: { email: emailOrId, filter: "", start_date: "", end_date: "" } }
  //         );
  //         console.log("fetchData getUserDetailByEmailID raw =>", r2.data);
  //         const raw = r2.data;
  //         userData =
  //           raw?.data ?? raw?.user ?? raw?.result ??
  //           (Array.isArray(raw) ? raw[0] : null) ??
  //           (typeof raw === "object" && raw?.status !== undefined
  //             ? Object.fromEntries(Object.entries(raw).filter(([k]) => k !== "status" && k !== "message"))
  //             : raw) ?? {};
  //       } catch (e) {
  //         console.log("getUserDetailByEmailID also failed =>", e);
  //       }
  //     }

  //     console.log("fetchData extracted =>", userData);

  //     let skills: string[] = [];
  //     const rawSkills2 = userData.skills;
  //     if (Array.isArray(rawSkills2)) {
  //       skills = rawSkills2.filter(Boolean);
  //     } else if (typeof rawSkills2 === "string" && rawSkills2.trim()) {
  //       skills = rawSkills2.split(",").map((s: string) => s.trim()).filter(Boolean);
  //     }

  //     localStorage.setItem("student_id", userData._id || "");
  //     localStorage.setItem("userid", userData._id || "");

  //     setResolvedCreator((prev: any) => ({
  //       ...(prev || {}),
  //       ...userData,
  //       user_email: userData.email || emailOrId,
  //       email: userData.email || emailOrId,
  //       name: userData.name || userData.first_name,
  //       profile_image: userData.user_image || userData.profile_image || userData.avatar || "",
  //       bio: userData.bio || userData.experience_summary || "",
  //       skills,
  //       total_learners: userData.total_learners ?? userData.learners_count ?? 0,
  //       mentor_id: userData._id,
  //       userid: userData._id,
  //     }));
  //     console.log("fetchData userDet =>", userData);
  //   } catch (error) {
  //     console.log(error);
  //   }
  // };

  // useEffect(() => {
  //   fetchData();
  // }, [mentorId]);

  // Helper function to build fallback image
  const buildFallbackImg = (title: string, seed: string) => {
    const stopWordsSet = new Set(["the", "a", "an", "and", "or", "but", "in", "on", "at", "to", "for", "of", "with", "by", "from", "is", "are", "was", "were", "be", "been", "how", "what", "why", "when", "which", "who", "that", "this", "these", "those", "will", "can", "do", "did", "has", "have", "had", "not", "its", "your", "our", "their", "about", "as", "into", "through", "up", "out", "so", "than", "then", "if", "we", "you", "i", "it"]);
    const keywords = title.toLowerCase().replace(/[^a-z0-9 ]/g, " ").split(" ").filter((w: string) => w.length > 3 && !stopWordsSet.has(w)).slice(0, 2).join(" ");
    const searchQuery = encodeURIComponent(keywords || "podcast");
    return `https://loremflickr.com/600/320/${searchQuery}?lock=${seed.slice(-4)}`;
  };

  // Helper function to get platform color for social posts
  const getPlatformColor = (platform: string) => {
    switch (platform?.toLowerCase()) {
      case 'twitter': return '#1DA1F2';
      case 'linkedin': return '#0A66C2';
      case 'facebook': return '#1877F2';
      default: return '#6b7280';
    }
  };

  const getCurrentClipPage = (episodeId: string) => {
    return clipPageIndex[episodeId] || 0;
  };

  const handleNextClips = (episodeId: string, totalClips: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const currentPage = getCurrentClipPage(episodeId);
    const totalPages = Math.ceil(totalClips / 4);
    if (currentPage + 1 < totalPages) {
      setClipPageIndex(prev => ({ ...prev, [episodeId]: currentPage + 1 }));
    }
  };

  const handlePrevClips = (episodeId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const currentPage = getCurrentClipPage(episodeId);
    if (currentPage > 0) {
      setClipPageIndex(prev => ({ ...prev, [episodeId]: currentPage - 1 }));
    }
  };

  const platform = (selectedSocialPost?.platform || "facebook").toLowerCase();

  const previewTitle =
    selectedSocialPost?.title ||
    selectedSocialPost?.post_title || selectedSocialPost?.suggestion_title ||
    "";

  const previewContent =
    selectedSocialPost?.content ||
    selectedSocialPost?.post_content ||
    "";

  const previewImage =
    selectedSocialPost?.image_url ||
    selectedSocialPost?.image_path ||
    "";

  const fetchData = async () => {
    try {
      const response = await getUserDetailsMutation({
        email: useremail || getCookie("user_email") || "",
        filter: "",
        start_date: "",
        end_date: "",
      });
      if ("data" in response) {
        setUserDetails(response.data.data);
      }
    } catch (error) { }
  };

  useEffect(() => {
    fetchData();

    const handleProfileUpdated = () => {
      fetchData();
    };

    window.addEventListener("profileUpdated", handleProfileUpdated);

    return () => {
      window.removeEventListener(
        "profileUpdated",
        handleProfileUpdated
      );
    };
  }, []);

  const handleCourseSelect = (courseId: string) => {
    setSelectedCourses((prev) =>
      prev.includes(courseId)
        ? prev.filter((id) => id !== courseId)
        : [...prev, courseId]
    );
  };

  const handleSelectAllCourses = () => {
    if (selectedCourses.length === courses.length) {
      setSelectedCourses([]);
    } else {
      setSelectedCourses(courses.map((c) => c._id));
    }
  };

  const handleUnpublishCourses = async () => {
    if (!selectedCourses.length || unpublishingCourses) return;

    try {
      setUnpublishingCourses(true);

      const courseIds = selectedCourses.join(",");

      const response = await axios.get(
        `${apiUrl}/api/prompts/unpublishCourseMultipleFromAuhtoriyPage?course_ids=${courseIds}`
      );

      console.log("Unpublish response =>", response.data);

      if (response.data.status === 'success') {
        toast.success(
          response?.data?.data || "Course(s) unpublished successfully"
        );
      } else {
        toast.error('Something went wrong, please try again later!');
      }

      // Refresh courses list
      // getCourselisting();
      getCourselisting(coursePage);

      // Clear selection
      setSelectedCourses([]);
    } catch (error: any) {
      console.log("Unpublish error =>", error);

      toast.error(
        error?.response?.data?.message ||
        "Failed to unpublish course(s)"
      );
    } finally {
      setUnpublishingCourses(false);
    }
  };

  const handleImageSelect = (imageId: string) => {
    setSelectedImages((prev) =>
      prev.includes(imageId)
        ? prev.filter((id) => id !== imageId)
        : [...prev, imageId]
    );
  };

  const handleSelectAllImages = () => {
    if (selectedImages.length === images.length) {
      setSelectedImages([]);
    } else {
      setSelectedImages(images.map((img: any) => img.id));
    }
  };

  const handleUnpublishImages = async () => {
    if (!selectedImages.length || unpublishingImages) return;

    try {
      setUnpublishingImages(true);

      const imageIds = selectedImages.join(",");

      const response = await axios.get(
        `${apiUrl}/api/prompts/unpublishImagesMultipleFromAuhtoriyPage?image_ids=${imageIds}`
      );

      console.log('unpublished images res=>', response.data);

      if (response.data.status === 'success') {
        toast.success(
          response?.data?.data || "Image(s) unpublished successfully"
        );
      } else {
        toast.error('Something went wrong, Please try again later!');
      }

      await fetchImages();

      setSelectedImages([]);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
        "Failed to unpublish image(s)"
      );
    } finally {
      setUnpublishingImages(false);
    }
  };

  useEffect(() => {
    setSocialSelectAll(
      socialPosts.length > 0 &&
      selectedSocialPosts.length === socialPosts.length
    );
  }, [selectedSocialPosts, socialPosts]);

  const handleUnpublishSelected = async () => {
    try {
      setUnpublishLoading(true);

      const response = await axios.post(
        `${apiUrl}/api/socialmedia/api/posts/unpublish_posts_multipe_from_authority_page?post_ids=${selectedSocialPosts}`,
        /*  {
           post_ids: selectedSocialPosts
         },
         {
           withCredentials: true
         } */
      );

      // console.log('unpublish social posts =>', response.data);

      toast.success(
        // `${selectedSocialPosts.length} post(s) unpublished`
        response?.data.message
      );

      setSelectedSocialPosts([]);
      setSocialSelectAll(false);

      fetchSocialPosts();
    } catch (error) {
      console.error(error);
      toast.error("Failed to unpublish posts");
    } finally {
      setUnpublishLoading(false);
    }
  };

  useEffect(() => {
    const blogList = Array.isArray(blogs) ? blogs : [];

    setBlogsSelectAll(
      blogList.length > 0 &&
      selectedBlogs.length === blogList.length
    );
  }, [selectedBlogs, blogs]);

  const handleUnpublishBlogs = async () => {
    try {
      setUnpublishBlogsLoading(true);

      const response = await axios.get(
        `${apiUrl}/api/prompts/unpublishBlogMultipleFromAuhtoriyPage?blog_ids=${selectedBlogs.join(",")}`,
        /* {
          params: {
            blog_ids: selectedBlogs.join(",")
          }
        } */
      );

      // console.log('unpublishing blogs res =>', response.data);

      if (response?.data?.status === 'success') {
        toast.success(
          /* `${selectedBlogs.length} blog(s) unpublished successfully` */
          response.data.data
        );
      } else {
        toast.error('Something went wrong, please try again later!');
      }

      setSelectedBlogs([]);
      setBlogsSelectAll(false);

      // refresh listing
      fetchBlogs();

    } catch (error) {
      console.error(error);
      toast.error("Failed to unpublish blogs");
    } finally {
      setUnpublishBlogsLoading(false);
    }
  };

  const handleUnpublishPodcasts = async () => {
    try {
      setUnpublishPodcastLoading(true);

      const response = await axios.get(
        `${apiUrl}/api/prompts/unpublishPodcastMultipleFromAuhtoriyPage`,
        {
          params: {
            podcast_ids: selectedPodcasts.join(",")
          }
        }
      );

      // console.log('unpublishing podcast response =>', response.data);
      if (response.data.status === 'success') {
        toast.success(
          // `${selectedPodcasts.length} podcast(s) unpublished`
          response.data.data
        );
      } else {
        toast.error('Something went wrong, please try again later!');
      }

      setSelectedPodcasts([]);
      setPodcastSelectAll(false);

      fetchEpisodeData();

    } catch (err) {
      console.error(err);
      toast.error("Failed to unpublish podcasts");
    } finally {
      setUnpublishPodcastLoading(false);
    }
  };

  const handleUnpublishClips = async () => {
    try {
      setUnpublishClipsLoading(true);

      const response = await axios.get(
        `${apiUrl}/api/prompts/unpublishClipsMultipleFromAuhtoriyPage`,
        {
          params: {
            clip_ids:
              selectedClips.join(",")
          }
        }
      );

      // console.log('unpublishing clips response =>', response.data);

      if (response.data.status === 'success') {
        toast.success(
          // `${selectedClips.length} clip(s) unpublished`
          response.data.data
        );
      } else {
        toast.error('Something went wrong, Please try again later!');
      }

      setSelectedClips([]);
      setClipsSelectAll(false);

      fetchEpisodeData();

    } catch (err) {
      console.error(err);
      toast.error("Failed to unpublish clips");
    } finally {
      setUnpublishClipsLoading(false);
    }
  };

  return (
    <>
      {interFontStyle}

      {/* Header — unchanged */}
      {/* activeItem !== "profile" && */(!activeItem || activeItem !== "profile" || isPublicProfile) && (
        <Header />
      )}
      {/* ── BODY ── */}
      <Box
        className="overlay-container instructor-profile-root"
        sx={{
          minHeight: "100vh",
          background: "transparent",
          fontFamily: "Inter, sans-serif",
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 6 },
            py: 4,
            maxWidth: 1100,
            mx: "auto",
          }}
        >
          {/* ================= PROFILE HEADER ================= */}
          <Box
            sx={{
              p: "24px",
              mb: 4,
              borderRadius: "12px",
              background: cardBg,
              border: `1px solid ${cardBorder}`,
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            }}
          >
            <Grid container spacing={3} alignItems="flex-start">
              {/* LEFT: Avatar + Info */}
              {userLoading || loadingProfile ? (
                <Grid item xs={12} md={8}>
                  <Stack direction="row" spacing={2.5}>
                    <Skeleton
                      variant="rounded"
                      width={132}
                      height={132}
                      sx={{ borderRadius: "8px", flexShrink: 0 }}
                    />
                    <Box width="100%">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Skeleton variant="text" width={140} height={28} />
                        <Skeleton variant="rounded" width={90} height={20} />
                      </Stack>
                      <Stack direction="row" spacing={4} mt={1.5}>
                        <Box>
                          <Skeleton variant="text" width={50} />
                          <Skeleton
                            variant="rounded"
                            width={60}
                            height={18}
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                        <Box>
                          <Skeleton variant="text" width={90} />
                          <Skeleton variant="text" width={60} />
                        </Box>
                        <Box>
                          <Skeleton variant="text" width={70} />
                          <Skeleton variant="text" width={60} />
                        </Box>
                      </Stack>
                    </Box>
                  </Stack>
                  <Box mt={2}>
                    <Skeleton variant="text" width={100} />
                    <Skeleton variant="text" />
                    <Skeleton variant="text" />
                    <Skeleton variant="text" width="80%" />
                  </Box>
                </Grid>
              ) : (
                <Grid item xs={12} md={8} sx={{ alignSelf: "flex-start" }}>
                  <Stack direction={{ xs: "column", sm: "row", md: "row" }} spacing={2.5} alignItems={{ xs: "center", sm: "flex-start" }}>
                    <Avatar
                      src={effectiveCreator?.profile_image || userDetails?.user_image}
                      sx={{
                        /* width: 132,
                        height: 132, */
                        width: { xs: 90, sm: 132 },
                        height: { xs: 90, sm: 132 },
                        borderRadius: "12px",
                        flexShrink: 0,
                        border: "2px solid #30363d",

                        // image position
                        "& img": {
                          objectFit: "cover",
                          objectPosition: "top center",
                        },
                      }}
                    />

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent={{ xs: "center", sm: "flex-start" }}
                        flexWrap="wrap"
                        mb={1}
                      >
                        <Typography
                          color="#e5e7eb"
                          fontWeight={700}
                          fontSize={18}
                          fontFamily="Inter, sans-serif"
                        >
                          {effectiveCreator?.name || effectiveCreator?.first_name}
                        </Typography>
                        <Chip
                          label="INSTRUCTOR"
                          size="small"
                          icon={<VerifiedTick />}
                          sx={{
                            height: 20,
                            fontSize: 11,
                            backgroundColor: "rgba(59,130,246,0.1)",
                            color: primary,
                            border: "1px solid rgba(59,130,246,0.3)",
                            borderRadius: "6px",
                            px: 0.5,
                            fontFamily: "Inter, sans-serif",
                            fontWeight: 600,
                            "& .MuiChip-icon": {
                              color: primary,
                              ml: "4px",
                              mr: "-2px",
                            },
                          }}
                        />
                      </Stack>

                      <Stack direction={{ xs: "column", sm: "row", md: "row" }} alignItems={{ xs: "center", sm: "flex-start" }} spacing={{ xs: 2, sm: 0 }}>
                        {/* Skills */}
                        {/* <Box sx={{ pr: 4, borderRight: "1px solid #e5e5e5" }}> */}
                        <Box
                          sx={{
                            pr: { xs: 0, sm: 4 },
                            pb: { xs: 2, sm: 0 },
                            // borderRight: { xs: "none", sm: "1px solid #e5e5e5" },
                            borderBottom: { xs: "1px solid #e5e5e5", sm: "none" },
                            width: { xs: "100%", sm: "auto" },
                            textAlign: { xs: "center", sm: "left" },
                          }}
                        >
                          <Typography
                            fontSize={12}
                            color="#6b7280"
                            mb={0.5}
                            fontFamily="Inter, sans-serif"
                            fontWeight={500}
                          >
                            Skills
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            flexWrap="wrap"
                            gap={0.5}
                          >
                            {(() => {
                              const raw = effectiveCreator?.skills;
                              const skillList: string[] = Array.isArray(raw)
                                ? raw
                                : typeof raw === "string" && raw.trim()
                                  ? raw
                                    .split(",")
                                    .map((s: string) => s.trim())
                                    .filter(Boolean)
                                  : [];
                              return skillList.length > 0 ? (
                                skillList.map((skill: string) => (
                                  <SkillChip key={skill} label={skill} />
                                ))
                              ) : (
                                <SkillChip label="NA" />
                              );
                            })()}
                          </Stack>
                        </Box>

                        {/* Total learners */}
                        {/* <Box sx={{ px: 4, borderRight: "1px solid #e5e5e5" }}> */}
                        {/* <Box
                          sx={{
                            px: { xs: 0, sm: 4 },
                            pb: { xs: 2, sm: 0 },
                            borderRight: { xs: "none", sm: "1px solid #e5e5e5" },
                            borderBottom: { xs: "1px solid #e5e5e5", sm: "none" },
                            width: { xs: "100%", sm: "auto" },
                            textAlign: "center",
                          }}
                        >
                          <Typography
                            fontSize={12}
                            color="#6b7280"
                            fontFamily="Inter, sans-serif"
                            fontWeight={500}
                          >
                            Total learners
                          </Typography>
                          <Typography
                            fontSize={16}
                            color="#e5e7eb"
                            fontWeight={700}
                            fontFamily="Inter, sans-serif"
                          >
                            {effectiveCreator?.total_learners}
                          </Typography>
                        </Box> */}

                        {/* Reviews */}
                        {/* <Box sx={{ pl: 4 }}> */}
                        {/* <Box
                          sx={{
                            pl: { xs: 0, sm: 4 },
                            textAlign: "center",
                            width: { xs: "100%", sm: "auto" },
                          }}
                        >
                          <Typography
                            fontSize={12}
                            color="#6b7280"
                            fontFamily="Inter, sans-serif"
                            fontWeight={500}
                          >
                            Reviews
                          </Typography>
                          <Typography
                            fontSize={16}
                            color="#e5e7eb"
                            fontWeight={700}
                            fontFamily="Inter, sans-serif"
                          >
                            {totalReviews}
                          </Typography>
                        </Box> */}
                      </Stack>
                    </Box>
                  </Stack>

                </Grid>
              )}

              {/* RIGHT: Share + Follow + Message — always one line, top-right */}
              <Grid item xs={12} md={4} sx={{ alignSelf: "flex-start" }}>
                {/* <Stack
                  direction="row"
                  spacing={1}
                  justifyContent={{ xs: "flex-start", md: "flex-end" }}
                  alignItems="center"
                  flexWrap="nowrap"
                  sx={{ whiteSpace: "nowrap" }}
                > */}
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent={{ xs: "center", md: "flex-end" }}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                  sx={{
                    mt: { xs: 2, md: 0 },
                    rowGap: 1,
                  }}
                >
                  {/* <Button
                    size="small"
                    onClick={() => {
                      const profileIdentifier =
                        effectiveCreator?.profile_id ||
                        effectiveCreator?.mentor_id ||
                        effectiveCreator?._id ||
                        effectiveCreator?.userid;
                      const shareUrl = `${window.location.origin}/profile/${encodeURIComponent(profileIdentifier)}`;
                      window.open(shareUrl, "_blank");
                    }}
                    sx={{
                      textTransform: "none",
                      background: "#21262d",
                      color: "#e5e7eb",
                      borderRadius: "6px !important",
                      px: 1.5,
                      py: 0,
                      height: 32,
                      minWidth: "unset",
                      fontSize: 12,
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 600,
                      border: "1px solid #30363d",
                      gap: "4px",
                      whiteSpace: "nowrap",
                      "&:hover": { background: "#30363d" },
                    }}
                  >
                    <ShareIcon sx={{ fontSize: "13px" }} />
                    Share Profile
                  </Button> */}

                  <Button
                    size="small"
                    onClick={() => {
                      window.open(
                        `${window.location.origin}/codegenie/courses-directory/${userDetails?.profile_id}`,
                        "_blank"
                      );
                    }}
                    sx={{
                      textTransform: "none",
                      background: "#21262d",
                      color: "#e5e7eb",
                      borderRadius: "6px !important",
                      px: 1.5,
                      py: 0,
                      height: 32,
                      minWidth: "unset",
                      fontSize: 12,
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 600,
                      border: "1px solid #30363d",
                      gap: "4px",
                      whiteSpace: "nowrap",
                      "&:hover": { background: "#30363d" },
                    }}
                  >
                    <ContentCopyIcon sx={{ fontSize: "13px" }} />
                    Open Public Course Directory
                  </Button>

                  <Button
                    size="small"
                    onClick={() => {
                      const profileIdentifier =
                        effectiveCreator?.profile_id ||
                        effectiveCreator?.mentor_id ||
                        effectiveCreator?._id ||
                        effectiveCreator?.userid;
                      const shareUrl = `${window.location.origin}/profile/${encodeURIComponent(profileIdentifier)}`;
                      if (navigator.clipboard && navigator.clipboard.writeText) {
                        navigator.clipboard.writeText(shareUrl).then(() => {
                          toast.success("Profile link copied to clipboard!");
                        }).catch(() => {
                          const el = document.createElement("textarea");
                          el.value = shareUrl;
                          el.style.position = "fixed";
                          el.style.opacity = "0";
                          document.body.appendChild(el);
                          el.focus();
                          el.select();
                          document.execCommand("copy");
                          document.body.removeChild(el);
                          toast.success("Profile link copied to clipboard!");
                        });
                      } else {
                        const el = document.createElement("textarea");
                        el.value = shareUrl;
                        el.style.position = "fixed";
                        el.style.opacity = "0";
                        document.body.appendChild(el);
                        el.focus();
                        el.select();
                        document.execCommand("copy");
                        document.body.removeChild(el);
                        toast.success("Profile link copied to clipboard!");
                      }
                    }}
                    sx={{
                      textTransform: "none",
                      background: "#21262d",
                      color: "#e5e7eb",
                      borderRadius: "6px !important",
                      px: 1.5,
                      py: 0,
                      height: 32,
                      minWidth: "unset",
                      fontSize: 12,
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 600,
                      border: "1px solid #30363d",
                      gap: "4px",
                      whiteSpace: "nowrap",
                      "&:hover": { background: "#30363d" },
                    }}
                  >
                    <ContentCopyIcon sx={{ fontSize: "13px" }} />
                    Copy Link
                  </Button>

                  {/* <Button
                    size="small"
                    onClick={handleFollowToggle}
                    disabled={loadingFollow}
                    sx={{
                      textTransform: "none",
                      background: isFollowing ? "#374151" : "#DE2900",
                      color: "#fff",
                      borderRadius: "6px !important",
                      minWidth: "unset",
                      px: 1.5,
                      py: 0,
                      height: 32,
                      fontSize: 12,
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      border: "1px solid transparent",
                      "&:hover": { background: "#b91c1c" },
                    }}
                  >
                    {loadingFollow
                      ? "..."
                      : isFollowing
                        ? "Unfollow"
                        : "+ Follow"}
                  </Button> */}

                  <Button
                    size="small"
                    onClick={() => setOpenEnquiryModal(true)}
                    className="button_leniar"
                    sx={{
                      textTransform: "none",
                      color: "#fff",
                      borderRadius: "6px !important",
                      gap: "4px",
                      minWidth: "unset",
                      px: 1.5,
                      py: 0,
                      height: 32,
                      fontSize: 12,
                      alignItems: "center",
                      display: "flex",
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      border: "1px solid transparent",
                    }}
                  >
                    <MailOutlinedIcon sx={{ fontSize: "14px" }} />
                    Message
                  </Button>
                </Stack>
              </Grid>

              {/* FULL WIDTH: About me — spans all 12 columns so no right-side gap */}
              {!userLoading && !loadingProfile && (
                <Grid item xs={12}>
                  <Box mt={0.5}>
                    <Typography
                      fontSize={13}
                      color="#e5e7eb"
                      fontWeight={700}
                      mb={0.5}
                      fontFamily="Inter, sans-serif"
                    >
                      About me
                    </Typography>
                    <Typography
                      fontSize={13}
                      color="#9ca3af"
                      lineHeight={1.7}
                      fontFamily="Inter, sans-serif"
                      sx={{ textAlign: "left", whiteSpace: "pre-line", }}
                    /* sx={{
                      textAlign: { xs: "center", sm: "left" },
                    }} */
                    >
                      {displayedBio}
                      {bio.length > minChars && (
                        <Typography
                          component="span"
                          color={primary}
                          fontFamily="Inter, sans-serif"
                          sx={{ cursor: "pointer", ml: 0.5 }}
                          onClick={() => setShowFullBio(!showFullBio)}
                        >
                          {showFullBio ? " less..." : " more..."}
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
          {/* /PROFILE HEADER */}

          {/* ================= MY COURSES ================= */}
          {courses?.length > 0 && (<>
            <Stack
              ref={coursesSectionRef}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
              sx={{ scrollMarginTop: "90px" }}
            >
              <Typography
                color="#e5e7eb"
                fontWeight={600}
                fontSize={16}
                letterSpacing="-0.01em"
                fontFamily="Inter, sans-serif"
              >
                Courses ({courseTotal/* courses?.length */ || 0})
              </Typography>
              {/* <Stack direction="row" spacing={2} alignItems="center">
                <Checkbox
                  checked={
                    courses.length > 0 &&
                    selectedCourses.length === courses.length
                  }
                  onChange={handleSelectAllCourses}
                  sx={{
                    color: "#4BA5EB",
                    "&.Mui-checked": {
                      color: "#4BA5EB",
                    },
                  }}
                />

                <Typography
                  sx={{
                    color: "#fff",
                    cursor: "pointer",
                  }}
                  onClick={handleSelectAllCourses}
                >
                  Select All
                </Typography>

                <Button
                  disabled={!selectedCourses.length}
                  onClick={handleUnpublishCourses}
                  sx={{
                    background: "#ff4d4f",
                    color: "#fff",
                    textTransform: "none",
                    borderRadius: "8px",
                    px: 2,
                    "&:hover": {
                      background: "#d9363e",
                    },
                  }}
                >
                  {unpublishingCourses ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : `Unpublish Courses (${selectedCourses.length})`}
                </Button>
              </Stack> */}
            </Stack>

            <Grid container spacing={{ xs: 1.5, sm: 2 }} mb={4} alignItems="stretch">
              {loadingCourses ? (
                Array.from(new Array(3)).map((_, i) => (
                  <Grid item xs={12} sm={6} md={4} key={i} sx={{ display: "flex" }}>
                    <CourseCardSkeleton />
                  </Grid>
                ))
              ) : (
                (() => {
                  // const real = visibleCourses || [];
                  const real = courses || [];
                  const sorted = [...real].sort((a, b) => {
                    const aHasImg = !!(a.image_path_small || a.image_path);
                    const bHasImg = !!(b.image_path_small || b.image_path);
                    return (bHasImg ? 1 : 0) - (aHasImg ? 1 : 0);
                  });
                  const dummyCount = Math.max(0, 3 - sorted.length);
                  return (
                    <>
                      {sorted.map((c: any, i: any) => (
                        <Grid item xs={12} sm={6} md={4} key={c._id || i} sx={{ display: "flex", width: "100%" }}>
                          <CourseCard course={c} selected={selectedCourses.includes(c._id)} onSelect={handleCourseSelect} return_domain={return_domain} loggedIn={loggedIn} />
                        </Grid>
                      ))}
                      {Array.from({ length: dummyCount }).map((_, i) => (
                        <Grid item xs={12} sm={6} md={4} key={"dc_" + i} sx={{ display: "flex", width: "100%" }}>
                          <DummyImageCard />
                        </Grid>
                      ))}
                    </>
                  );
                })()
              )}
            </Grid>

            {/* View more / View less — Courses */}
            {/* {courses?.length > 3 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: -1, mb: 3 }}>
                <Typography
                  fontSize={13}
                  color={primary}
                  fontFamily="Inter, sans-serif"
                  sx={{
                    cursor: "pointer",
                    px: 2,
                    py: 0.75,
                    borderRadius: "8px",
                    border: `1px solid ${primary}22`,
                    background: `${primary}11`,
                    transition: "background 0.2s",
                    "&:hover": { background: `${primary}22` },
                  }}
                  onClick={() => setShowAllCourses((prev) => !prev)}
                >
                  {showAllCourses ? "View less ↑" : "View more ↓"}
                </Typography>
              </Box>
            )} */}
            {/* Pagination — Courses */}
            {courseTotalPages > 1 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: -1,
                  mb: 4,
                }}
              >
                <Pagination
                  count={courseTotalPages}
                  page={coursePage}
                  onChange={handleCoursePageChange}
                  siblingCount={1}
                  boundaryCount={1}
                  color="primary"
                  shape="rounded"
                  renderItem={(item: any) => (
                    <PaginationItem
                      {...item}
                      sx={{
                        color: "#d1d5db",
                        borderColor: "#30363d",
                        background: "#0d1117",

                        "&.Mui-selected": {
                          background: primary,
                          color: "#fff",
                          "&:hover": {
                            background: primary,
                          },
                        },

                        "&:hover": {
                          background: `${primary}22`,
                        },
                      }}
                    />
                  )}
                />
              </Box>
            )}
          </>)}

          {/* ================= PODCAST EPISODES with CLIPS ================= */}
          {(() => {
            const hasRealEpisodes = episodesWithClips && episodesWithClips.length > 0;

            return (
              <>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                  mt={4}
                >
                  <Typography
                    color="#e5e7eb"
                    fontWeight={600}
                    fontSize={16}
                    letterSpacing="-0.01em"
                    fontFamily="Inter, sans-serif"
                  >
                    Podcast Episodes ({hasRealEpisodes ? episodesWithClips.length : 0})
                  </Typography>
                  {/* <Stack direction="row" spacing={2} alignItems="center">

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={podcastSelectAll}
                          onChange={(e) => {
                            const checked = e.target.checked;

                            if (checked) {
                              setSelectedPodcasts(
                                episodesWithClips.map(
                                  (episode: any) => episode._id || episode.campaign_id
                                )
                              );
                            } else {
                              setSelectedPodcasts([]);
                            }

                            setPodcastSelectAll(checked);
                          }}
                        />
                      }
                      label="Select All"
                    />
                    <Button
                      disabled={!selectedPodcasts.length || unpublishPodcastLoading}
                      onClick={handleUnpublishPodcasts}
                      sx={{
                        background: "#ff4d4f",
                        color: "#fff",
                        textTransform: "none",
                        borderRadius: "8px",
                        px: 2,
                        minWidth: 170,
                        "&:hover": {
                          background: "#d9363e",
                        },
                      }}
                    >
                      {unpublishPodcastLoading ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        `Unpublish Podcasts (${selectedPodcasts.length})`
                      )}
                    </Button>

                  </Stack> */}
                </Stack>

                {loadingEpisodes ? (
                  <Grid container spacing={3}>
                    {[1, 2, 3].map((i) => (
                      <Grid item xs={12} key={i}>
                        <Skeleton variant="rounded" height={300} sx={{ bgcolor: "#21262d", borderRadius: "12px" }} />
                      </Grid>
                    ))}
                  </Grid>
                ) : !hasRealEpisodes ? (
                  <Paper
                    sx={{
                      p: 3,
                      borderRadius: "12px",
                      background: cardBg,
                      border: `1px solid ${cardBorder}`,
                      mb: 4,
                      textAlign: "center",
                    }}
                  >
                    <Typography fontSize={13} color="#6b7280" fontFamily="Inter, sans-serif">
                      No podcast episodes available
                    </Typography>
                  </Paper>
                ) : (
                  <>
                    {visibleEpisodes.map((episode: any, idx: number) => {
                      // console.log('episodes res>', episode);
                      const episodeId = episode._id || episode.campaign_id || idx;
                      const epTitle = episode.title || `Episode ${idx + 1}`;
                      const epImg = episode.thump_image_path || buildFallbackImg(epTitle, String(episodeId));
                      const episodeClips = episode.clips || [];
                      const isLoadingClips = loadingClipsForEpisode[episode.campaign_id || episode._id || ""];
                      const currentClipPage = getCurrentClipPage(String(episodeId));
                      const clipsPerPage = 4;
                      const totalClipPages = Math.ceil(episodeClips.length / clipsPerPage);
                      const displayedClips = episodeClips.slice(
                        currentClipPage * clipsPerPage,
                        (currentClipPage + 1) * clipsPerPage
                      );

                      return (
                        <Paper
                          key={episodeId}
                          sx={{
                            mb: 3,
                            borderRadius: "12px",
                            background: cardBg,
                            border: `1px solid ${cardBorder}`,
                            overflow: "hidden",
                            transition: "box-shadow 0.2s ease",
                            "&:hover": {
                              boxShadow: "0 4px 20px rgba(59,130,246,0.15)",
                            },
                          }}
                        >
                          {/* Episode Header - Clickable to go to episode details */}
                          <Box
                            sx={{
                              // p: 2.5,
                              p: {
                                xs: 1.2,
                                sm: 2.5,
                              },
                              cursor: "pointer",
                              "&:hover": { bgcolor: "rgba(59,130,246,0.05)" },
                            }}
                            /* onClick={() => {
                            /* onClick={() => {
                              localStorage.setItem("episodeId", episodeId);
                              navigate("/codegenie/episode-detail", {
                                state: { episodeId: episodeId, episode: episode }
                              });
}} */
                            onClick={(e) => {
                              e.stopPropagation();
                              if (episode.original_file) handleVideoClick(episode.original_file);
                            }}
                          >
                            <Grid container /* spacing={2} */ spacing={{
                              xs: 1,
                              sm: 2,
                            }} alignItems="center" sx={{ width: "100%", margin: 0 }}>
                              <Grid item xs={12} sm={3} sx={{ width: "100%" }}>
                                <Box sx={{ position: "relative" }}>
                                  {/* <Checkbox
                                    checked={selectedPodcasts.includes(
                                      episode._id || episode.campaign_id
                                    )}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      const id =
                                        episode._id || episode.campaign_id;

                                      const checked = e.target.checked;

                                      setSelectedPodcasts((prev) =>
                                        checked
                                          ? [...prev, id]
                                          : prev.filter((x) => x !== id)
                                      );
                                    }}
                                    sx={{
                                      position: "absolute",
                                      top: 5,
                                      left: 5,
                                      zIndex: 2,
                                      color: "#fff",
                                      background: "rgba(0,0,0,.4)",
                                      borderRadius: "50%",
                                    }}
                                  /> */}
                                  <Box
                                    component="img"
                                    src={epImg}
                                    onError={(e: any) => {
                                      const img = e.target as HTMLImageElement;
                                      if (!img.dataset.fallback) {
                                        img.dataset.fallback = "1";
                                        const num = String(episodeId).split("").reduce((acc: number, c: string) => acc + c.charCodeAt(0), 0) % 1000;
                                        img.src = `https://picsum.photos/id/${num}/600/320`;
                                      }
                                    }}
                                    sx={{
                                      /* width: "100%",
                                      height: 120,
                                      objectFit: "cover",
                                      borderRadius: "10px", */
                                      width: "100%",
                                      maxWidth: "100%",
                                      display: "block",
                                      height: {
                                        xs: 180,
                                        sm: 120,
                                      },
                                      objectFit: "cover",
                                      borderRadius: "10px",
                                    }}
                                    alt={epTitle}
                                  />
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={9}>
                                <Typography
                                  variant="h6"
                                  fontWeight={700}
                                  color="#e5e7eb"
                                  fontFamily="Inter, sans-serif"
                                  mb={0.5}
                                >
                                  {epTitle}
                                </Typography>
                                {episode.description && (
                                  <Typography
                                    fontSize={13}
                                    color="#9ca3af"
                                    fontFamily="Inter, sans-serif"
                                    sx={{
                                      display: "-webkit-box",
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {episode.description}
                                  </Typography>
                                )}
                                <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1}>
                                  {episode.created_at && (
                                    <Typography
                                      fontSize={11}
                                      color="#6b7280"
                                      fontFamily="Inter, sans-serif"
                                    >
                                      {new Date(episode.created_at).toLocaleDateString("en-US", {
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                      })}
                                    </Typography>
                                  )}

                                  {/* View Clips Button - Inside episode box, navigates to episode details */}
                                  {episodeClips.length > 0 && (
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      spacing={0.5}
                                      sx={{ cursor: "pointer" }}
                                      /* onClick={(e) => {
                                        e.stopPropagation();
                                        localStorage.setItem("episodeId", episodeId);
                                        navigate("/codegenie/episode-detail", {
                                          state: { episodeId: episodeId, episode: episode }
                                        });
                                      }} */
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (episode.original_file) handleVideoClick(episode.original_file);
                                      }}
                                    >
                                      <Typography
                                        fontSize={12}
                                        color="#3b82f6"
                                        fontWeight={600}
                                        fontFamily="Inter, sans-serif"
                                        sx={{ cursor: "pointer" }}
                                      >
                                        View Podcast{/*  ({episodeClips.length}) */}
                                      </Typography>
                                      <EastOutlinedIcon sx={{ color: "#3b82f6", fontSize: 16 }} />
                                    </Stack>
                                  )}
                                </Stack>
                              </Grid>
                            </Grid>
                          </Box>

                          {/* Clips Section for this Episode */}
                          {isLoadingClips ? (
                            <Box sx={{ p: 2.5, pt: 0, borderTop: `1px solid ${cardBorder}` }}>
                              <Grid container spacing={1.5} sx={{ mt: 1.5 }}>
                                {[1, 2, 3, 4].map((i) => (
                                  <Grid item xs={6} sm={3} key={i}>
                                    <Skeleton variant="rounded" height={120} sx={{ bgcolor: "#21262d", borderRadius: "8px" }} />
                                  </Grid>
                                ))}
                              </Grid>
                            </Box>
                          ) : episodeClips.length === 0 ? (
                            <Box
                              sx={{
                                p: 2.5,
                                pt: 1.5,
                                borderTop: `1px solid ${cardBorder}`,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <Typography fontSize={13} color="#6b7280" fontFamily="Inter, sans-serif">
                                No clips available
                              </Typography>
                            </Box>
                          ) : (
                            <Box sx={{ p: 2.5, pt: 0, borderTop: `1px solid ${cardBorder}` }}>
                              {/* Clips Header */}
                              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5} sx={{ mt: 1.5 }}>
                                <Typography fontSize={13} fontWeight={600} color="#9ca3af" fontFamily="Inter, sans-serif">
                                  Clips ({episodeClips.length})
                                </Typography>
                                {/*  <Stack
                                  direction="row"
                                  spacing={2}
                                  alignItems="center"
                                >
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={
                                          episodeClips.length > 0 &&
                                          episodeClips.every((clip: any) =>
                                            selectedClips.includes(clip.campaign_id)
                                          )
                                        }
                                        onChange={(e) => {
                                          const checked = e.target.checked;

                                          const clipIds =
                                            episodeClips.map(
                                              (clip: any) => clip.campaign_id
                                            );

                                          if (checked) {
                                            setSelectedClips((prev: any) => [
                                              ...prev,
                                              ...clipIds.filter(
                                                (id: any) => !prev.includes(id)
                                              ),
                                            ]);
                                          } else {
                                            setSelectedClips((prev) =>
                                              prev.filter(
                                                (id) =>
                                                  !clipIds.includes(id)
                                              )
                                            );
                                          }
                                        }}
                                      />
                                    }
                                    label="Select All"
                                  />

                                  <Button
                                    disabled={!selectedClips.length || unpublishClipsLoading}
                                    onClick={handleUnpublishClips}
                                    sx={{
                                      background: "#ff4d4f",
                                      color: "#fff",
                                      textTransform: "none",
                                      borderRadius: "8px",
                                      px: 2,
                                      minWidth: 170,
                                      "&:hover": {
                                        background: "#d9363e",
                                      },
                                    }}
                                  >
                                    {unpublishClipsLoading ? (
                                      <CircularProgress size={18} color="inherit" />
                                    ) : (
                                      `Unpublish Clips (${selectedClips.length})`
                                    )}
                                  </Button>
                                </Stack> */}
                              </Stack>
                              <Box sx={{ position: "relative" }}>
                                {/* Left Arrow - positioned on left side of clips */}
                                {totalClipPages > 1 && currentClipPage > 0 && (
                                  <Box
                                    onClick={(e) => handlePrevClips(String(episodeId), e)}
                                    sx={{
                                      position: "absolute",
                                      left: -20,
                                      top: "50%",
                                      transform: "translateY(-50%)",
                                      width: 36,
                                      height: 36,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      borderRadius: "50%",
                                      background: "#1e293b",
                                      color: "#e5e7eb",
                                      cursor: "pointer",
                                      zIndex: 10,
                                      transition: "all 0.2s",
                                      border: `1px solid ${cardBorder}`,
                                      "&:hover": {
                                        background: "#3b82f6",
                                        color: "#fff",
                                        transform: "translateY(-50%) scale(1.05)",
                                      },
                                    }}
                                  >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="15 18 9 12 15 6"></polyline>
                                    </svg>
                                  </Box>
                                )}

                                {/* Right Arrow - positioned on right side of clips */}
                                {totalClipPages > 1 && currentClipPage + 1 < totalClipPages && (
                                  <Box
                                    onClick={(e) => handleNextClips(String(episodeId), episodeClips.length, e)}
                                    sx={{
                                      position: "absolute",
                                      right: -20,
                                      top: "50%",
                                      transform: "translateY(-50%)",
                                      width: 36,
                                      height: 36,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      borderRadius: "50%",
                                      background: "#1e293b",
                                      color: "#e5e7eb",
                                      cursor: "pointer",
                                      zIndex: 10,
                                      transition: "all 0.2s",
                                      border: `1px solid ${cardBorder}`,
                                      "&:hover": {
                                        background: "#3b82f6",
                                        color: "#fff",
                                        transform: "translateY(-50%) scale(1.05)",
                                      },
                                    }}
                                  >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="9 18 15 12 9 6"></polyline>
                                    </svg>
                                  </Box>
                                )}

                                {/* Clips Grid */}
                                <Grid container spacing={1.5} sx={{ position: "relative", px: totalClipPages > 1 ? 2 : 0 }}>
                                  {displayedClips.map((clip: any, clipIdx: number) => {
                                    const clipTitle = clip?.title || `Clip ${clipIdx + 1}`;
                                    const clipThumb = clip?.thumbnail || clip?.thump_image_path || "";
                                    const clipUrl = clip?.video_path || clip?.media_url || clip?.url || "";

                                    return (
                                      <Grid item xs={6} sm={3} key={clip._id || clipIdx} sx={{ display: "flex" }}>
                                        <Box
                                          sx={{
                                            width: "100%",
                                            borderRadius: "10px",
                                            overflow: "hidden",
                                            border: `1px solid ${cardBorder}`,
                                            background: "#0d1117",
                                            cursor: clipUrl ? "pointer" : "default",
                                            transition: "transform 0.2s, box-shadow 0.2s, border-color 0.2s",
                                            display: "flex",
                                            flexDirection: "column",
                                            "&:hover": clipUrl ? {
                                              transform: "translateY(-2px)",
                                              boxShadow: "0 6px 16px rgba(59,130,246,0.2)",
                                              borderColor: primary,
                                            } : {},
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (clipUrl) handleVideoClick(clipUrl);
                                          }}
                                        >
                                          <Box sx={{
                                            position: "relative",
                                            width: "100%",
                                            height: 110,
                                            flexShrink: 0,
                                            backgroundColor: "#1e293b",
                                          }}>
                                            {/*  <Checkbox
                                              checked={selectedClips.includes(
                                                clip.campaign_id
                                              )}
                                              onClick={(e) => e.stopPropagation()}
                                              onChange={(e) => {
                                                const checked = e.target.checked;

                                                setSelectedClips((prev) =>
                                                  checked
                                                    ? [...prev, clip.campaign_id]
                                                    : prev.filter(
                                                      (id) => id !== clip.campaign_id
                                                    )
                                                );
                                              }}
                                              sx={{
                                                position: "absolute",
                                                top: 4,
                                                left: 4,
                                                zIndex: 2,
                                                color: "#fff",
                                                background: "rgba(0,0,0,.4)",
                                                borderRadius: "50%",
                                              }}
                                            /> */}
                                            {clipThumb ? (
                                              <Box
                                                component="img"
                                                src={clipThumb}
                                                onError={(e: any) => {
                                                  const img = e.target as HTMLImageElement;
                                                  if (!img.dataset.fallback) {
                                                    img.dataset.fallback = "1";
                                                    img.src = dummyBlogImg as string;
                                                  }
                                                }}
                                                sx={{
                                                  width: "100%",
                                                  height: "100%",
                                                  objectFit: "cover",
                                                  display: "block",
                                                }}
                                                alt={clipTitle}
                                              />
                                            ) : (
                                              <Box sx={{
                                                width: "100%",
                                                height: "100%",
                                                bgcolor: "#1e293b",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                              }}>
                                                <PlayCircleOutlineOutlinedIcon sx={{ fontSize: 40, color: "#4b5563" }} />
                                              </Box>
                                            )}
                                            {clipUrl && (
                                              <Box sx={{
                                                position: "absolute",
                                                inset: 0,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                background: "rgba(0,0,0,0.3)",
                                                opacity: 0,
                                                transition: "opacity 0.2s",
                                                "&:hover": { opacity: 1 },
                                              }}>
                                                <PlayCircleOutlineOutlinedIcon sx={{ fontSize: 42, color: "#fff" }} />
                                              </Box>
                                            )}
                                          </Box>

                                          <Box sx={{ p: 1.5, minHeight: 60, display: "flex", alignItems: "flex-start" }}>
                                            <Typography
                                              fontSize={12}
                                              fontWeight={500}
                                              color="#e5e7eb"
                                              fontFamily="Inter, sans-serif"
                                              sx={{
                                                display: "-webkit-box",
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                                lineHeight: 1.4,
                                                wordBreak: "break-word",
                                              }}
                                            >
                                              {clipTitle}
                                            </Typography>
                                          </Box>
                                        </Box>
                                      </Grid>
                                    );
                                  })}
                                </Grid>

                                {/* Page indicator - centered below clips */}
                                {totalClipPages > 1 && (
                                  <Stack direction="row" justifyContent="center" alignItems="center" spacing={1} mt={2}>
                                    <Typography
                                      fontSize={11}
                                      color="#6b7280"
                                      fontFamily="Inter, sans-serif"
                                    >
                                      Page {currentClipPage + 1} of {totalClipPages}
                                    </Typography>
                                  </Stack>
                                )}
                              </Box>
                            </Box>
                          )}
                        </Paper>
                      );
                    })}
                  </>
                )}

                {/* View more / View less — Episodes */}
                {hasRealEpisodes && episodesWithClips.length > 3 && (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 1, mb: 4 }}>
                    <Typography
                      fontSize={13}
                      color={primary}
                      fontFamily="Inter, sans-serif"
                      sx={{
                        cursor: "pointer",
                        px: 2,
                        py: 0.75,
                        borderRadius: "8px",
                        border: `1px solid ${primary}22`,
                        background: `${primary}11`,
                        transition: "background 0.2s",
                        "&:hover": { background: `${primary}22` },
                      }}
                      onClick={() => setShowAllEpisodes((prev) => !prev)}
                    >
                      {showAllEpisodes ? "View less ↑" : "View more ↓"}
                    </Typography>
                  </Box>
                )}
              </>
            );
          })()}

          {/* ================= BLOGS ================= */}
          {(() => {
            // const realBlogs = blogs && blogs.length > 0 ? blogs : [];
            const realBlogs = Array.isArray(blogs) ? blogs : [];
            const dummyBlogCount = Math.max(0, 3 - realBlogs?.length);
            const visibleBlogs = showAllBlogs ? realBlogs : realBlogs?.slice(0, 3);

            return (
              /* realBlogs?.length > 0 && ( */<>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                  mt={4}
                >
                  <Typography
                    color="#e5e7eb"
                    fontWeight={600}
                    fontSize={16}
                    letterSpacing="-0.01em"
                    fontFamily="Inter, sans-serif"
                  >
                    Blogs ({realBlogs.length})
                  </Typography>

                  {/* <Stack direction="row" spacing={2} alignItems="center">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={blogsSelectAll}
                          onChange={(e) => {
                            const checked = e.target.checked;

                            if (checked) {
                              setSelectedBlogs(
                                realBlogs.map((blog: any) => blog._id)
                              );
                            } else {
                              setSelectedBlogs([]);
                            }

                            setBlogsSelectAll(checked);
                          }}
                        />
                      }
                      label="Select All"
                    />

                    <Button
                      disabled={!selectedBlogs.length || unpublishBlogsLoading}
                      onClick={handleUnpublishBlogs}
                      sx={{
                        background: "#ff4d4f",
                        color: "#fff",
                        textTransform: "none",
                        borderRadius: "8px",
                        px: 2,
                        minWidth: 170,
                        "&:hover": {
                          background: "#d9363e",
                        },
                      }}
                    >
                      {unpublishBlogsLoading ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        `Unpublish Blogs (${selectedBlogs.length})`
                      )}
                    </Button>
                  </Stack> */}
                </Stack>

                {loadingBlogs ? (
                  <Grid container spacing={2} mb={4}>
                    {Array.from(new Array(3)).map((_, i) => (
                      <Grid item xs={12} sm={6} md={4} key={i}>
                        <Paper sx={{ p: 1.5, borderRadius: "12px", background: cardBg, border: `1px solid ${cardBorder}` }}>
                          <Skeleton variant="rectangular" height={160} sx={{ borderRadius: "10px" }} />
                          <Skeleton variant="text" height={24} sx={{ mt: 1.5 }} />
                          <Skeleton variant="text" height={18} />
                          <Skeleton variant="text" width="70%" height={18} />
                          <Stack direction="row" spacing={1} mt={1}>
                            <Skeleton variant="rounded" width={60} height={18} />
                            <Skeleton variant="rounded" width={60} height={18} />
                          </Stack>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Grid container spacing={2} mb={4}>
                    {visibleBlogs?.map((blog: any, i: number) => {
                      const blogImg = blog.blog_image_path || blog.blog_image_path_small || dummyBlogImg;
                      const blogTitle = blog.blog_title || blog.title || "";
                      const stopWordsSet = new Set(["the", "a", "an", "and", "or", "but", "in", "on", "at", "to", "for", "of", "with", "by", "from", "is", "are", "was", "were", "be", "been", "how", "what", "why", "when", "which", "who", "that", "this", "these", "those", "will", "can", "do", "did", "has", "have", "had", "not", "its", "your", "our", "their", "about", "as", "into", "through", "up", "out", "so", "than", "then", "if", "we", "you", "i", "it"]);
                      const keywords = blogTitle.toLowerCase().replace(/[^a-z0-9 ]/g, " ").split(" ").filter((w: string) => w.length > 3 && !stopWordsSet.has(w)).slice(0, 2).join(" ");
                      const fallbackUrl = `https://loremflickr.com/600/320/${encodeURIComponent(keywords || "business")}?lock=${String(blog._id || i).slice(-4)}`;
                      const finalSrc = blogImg || fallbackUrl;

                      return (
                        <Grid item xs={12} sm={6} md={4} key={blog._id || i} sx={{ display: "flex" }}>
                          <Paper
                            sx={{
                              p: 1.5,
                              borderRadius: "12px",
                              background: cardBg,
                              border: `1px solid ${cardBorder}`,
                              width: "100%",
                              display: "flex",
                              flexDirection: "column",
                              fontFamily: "Inter, sans-serif",
                              transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                              "&:hover": {
                                boxShadow: "0 4px 20px rgba(59,130,246,0.15)",
                                borderColor: "#3b82f6",
                              },
                            }}
                          >
                            <Box sx={{ position: "relative", width: "100%", height: 160, borderRadius: "10px", overflow: "hidden", flexShrink: 0, bgcolor: "#1e293b" }}>
                              {/* <Checkbox
                                checked={selectedBlogs.includes(blog._id)}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  const checked = e.target.checked;

                                  setSelectedBlogs((prev) =>
                                    checked
                                      ? [...prev, blog._id]
                                      : prev.filter((id) => id !== blog._id)
                                  );
                                }}
                                sx={{
                                  position: "absolute",
                                  top: 6,
                                  left: 6,
                                  zIndex: 2,
                                  color: "#fff",
                                  background: "rgba(0,0,0,0.4)",
                                  borderRadius: "50%",
                                  "&.Mui-checked": {
                                    color: "#3b82f6",
                                  },
                                }}
                              /> */}
                              <Box
                                component="img"
                                src={finalSrc}
                                onError={(e: any) => {
                                  const img = e.target as HTMLImageElement;
                                  if (!img.dataset.fallback2) {
                                    img.dataset.fallback2 = "1";
                                    const num = String(blog._id || i).split("").reduce((acc: number, c: string) => acc + c.charCodeAt(0), 0) % 1000;
                                    img.src = `https://picsum.photos/id/${num}/600/320`;
                                  } else {
                                    img.style.display = "none";
                                  }
                                }}
                                sx={{
                                  position: "absolute",
                                  inset: 0,
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  maxWidth: '100%'
                                }}
                                alt={blogTitle}
                              />
                            </Box>

                            <Typography
                              mt={1.5}
                              fontSize={13}
                              fontWeight={600}
                              color="#e5e7eb"
                              lineHeight={1.4}
                              fontFamily="Inter, sans-serif"
                              sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                flexGrow: 1,
                              }}
                            >
                              {blogTitle}
                            </Typography>

                            <Typography
                              mt={0.75}
                              fontSize={12}
                              color="#9ca3af"
                              fontFamily="Inter, sans-serif"
                              lineHeight={1.5}
                              sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {blog.blog_summary || blog.blog_description || blog.description || ""}
                            </Typography>

                            <Stack direction="row" spacing={0.5} mt={1} flexWrap="wrap" gap={0.5}>
                              {(blog.tags || []).slice(0, 3).map((tag: string) => (
                                <Chip
                                  key={tag}
                                  label={tag}
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: 10,
                                    border: "1px solid #30363d",
                                    background: "#0d1117",
                                    color: "#9ca3af",
                                    borderRadius: "4px",
                                    fontFamily: "Inter, sans-serif",
                                  }}
                                />
                              ))}
                            </Stack>

                            {blog.created_at && (
                              <Typography mt={0.75} fontSize={11} color="#6b7280" fontFamily="Inter, sans-serif">
                                {new Date(blog.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                              </Typography>
                            )}

                            {/* View Blog button - same style as View Course button */}
                            <Stack direction="row" spacing={1} mt={1.5} alignItems="center">
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.5}
                                sx={{ cursor: "pointer", ml: "auto" }}
                                onClick={() => {
                                  const blogId = blog._id;
                                  const productId = blog.product_id || "";
                                  const mode = blog.mode || "";
                                  const statichtml = blog.statichtml || false;
                                  const canonical_url = blog.canonical_url || "";
                                  let url = `/PreviewBlogs1/${blogId}?campId=${productId}`;
                                  if (blog.public_url)
                                    url = blog.public_url;
                                  // if (loggedIn === 'true') {
                                  //   if ((mode === "Live" || (mode === "draft" && statichtml === true)) && canonical_url) {
                                  //     url = canonical_url + `?campId=${productId}`;
                                  //   }
                                  // } else {
                                  //   url = canonical_url + `/PreviewBlogs1/${blog.url}`;
                                  // }
                                  window.open(url, "_blank");
                                }}
                              >
                                <Typography
                                  fontSize={12}
                                  color="#3b82f6"
                                  fontWeight={600}
                                  fontFamily="Inter, sans-serif"
                                  sx={{ cursor: "pointer" }}
                                >
                                  View Blog
                                </Typography>
                                <EastOutlinedIcon sx={{ color: "#3b82f6", fontSize: 16 }} />
                              </Stack>
                            </Stack>
                          </Paper>
                        </Grid>
                      );
                    })}
                    {visibleBlogs?.length === 0 && Array.from({ length: dummyBlogCount }).map((_, i) => (
                      <Grid item xs={12} sm={6} md={4} key={"db_dummy_" + i} sx={{ display: "flex" }}>
                        <DummyImageCard variant="blog" />
                      </Grid>
                    ))}
                  </Grid>
                )}

                {/* View more / View less — Blogs */}
                {realBlogs.length > 3 && (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 1, mb: 2 }}>
                    <Typography
                      fontSize={13}
                      color={primary}
                      fontFamily="Inter, sans-serif"
                      sx={{
                        cursor: "pointer",
                        px: 2,
                        py: 0.75,
                        borderRadius: "8px",
                        border: `1px solid ${primary}22`,
                        background: `${primary}11`,
                        transition: "background 0.2s",
                        "&:hover": { background: `${primary}22` },
                      }}
                      onClick={() => setShowAllBlogs((prev) => !prev)}
                    >
                      {showAllBlogs ? "View less ↑" : "View more ↓"}
                    </Typography>
                  </Box>
                )}
              </>/* ) */
            );
          })()}

          {/* ================= SOCIAL POSTS ================= */}
          {(() => {
            const postsPerPage = 4;
            const totalSocialPages = Math.ceil(socialPosts.length / postsPerPage);
            const displayedSocialPosts = socialPosts.slice(
              socialPostsPageIndex * postsPerPage,
              (socialPostsPageIndex + 1) * postsPerPage
            );
            const visibleSocialPosts = showAllSocialPosts ? socialPosts : displayedSocialPosts;

            const handleNextSocialPosts = () => {
              if (socialPostsPageIndex + 1 < totalSocialPages) {
                setSocialPostsPageIndex(prev => prev + 1);
              }
            };

            const handlePrevSocialPosts = () => {
              if (socialPostsPageIndex > 0) {
                setSocialPostsPageIndex(prev => prev - 1);
              }
            };

            return (
              <>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                  mt={4}
                >
                  <Typography
                    color="#e5e7eb"
                    fontWeight={600}
                    fontSize={16}
                    letterSpacing="-0.01em"
                    fontFamily="Inter, sans-serif"
                  >
                    Social Posts ({socialPosts.length})
                  </Typography>

                  {/* <Stack direction="row" spacing={2} alignItems="center">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={socialSelectAll}
                          onChange={(e) => {
                            const checked = e.target.checked;

                            setSocialSelectAll(checked);

                            if (checked) {
                              setSelectedSocialPosts(
                                socialPosts.map((p: any) => p.id)
                              );
                            } else {
                              setSelectedSocialPosts([]);
                            }
                          }}
                        />
                      }
                      label="Select All"
                    />

                    <Button
                      disabled={!selectedSocialPosts.length || unpublishingImages}
                      onClick={handleUnpublishSelected}
                      sx={{
                        background: "#ff4d4f",
                        color: "#fff",
                        textTransform: "none",
                        borderRadius: "8px",
                        px: 2,
                        minWidth: 170,
                        "&:hover": {
                          background: "#d9363e",
                        },
                      }}
                    >
                      {unpublishLoading ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        `Unpublish Posts (${selectedSocialPosts.length})`
                      )}
                    </Button>
                  </Stack> */}
                </Stack>

                {loadingSocialPosts ? (
                  <Grid container spacing={2} mb={4}>
                    {Array.from(new Array(4)).map((_, i) => (
                      <Grid item xs={6} sm={3} key={i} sx={{ display: "flex" }}>
                        <Paper sx={{ p: 1.5, borderRadius: "12px", background: cardBg, border: `1px solid ${cardBorder}`, width: "100%" }}>
                          <Skeleton variant="rectangular" height={140} sx={{ borderRadius: "10px" }} />
                          <Skeleton variant="text" height={20} sx={{ mt: 1 }} />
                          <Skeleton variant="text" height={16} width="60%" />
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box sx={{ position: "relative" }}>
                    {/* Left Arrow */}
                    {totalSocialPages > 1 && socialPostsPageIndex > 0 && (
                      <Box
                        onClick={handlePrevSocialPosts}
                        sx={{
                          position: "absolute",
                          left: -20,
                          top: "50%",
                          transform: "translateY(-50%)",
                          width: 36,
                          height: 36,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "50%",
                          background: "#1e293b",
                          color: "#e5e7eb",
                          cursor: "pointer",
                          zIndex: 10,
                          transition: "all 0.2s",
                          border: `1px solid ${cardBorder}`,
                          "&:hover": {
                            background: "#3b82f6",
                            color: "#fff",
                            transform: "translateY(-50%) scale(1.05)",
                          },
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="15 18 9 12 15 6"></polyline>
                        </svg>
                      </Box>
                    )}

                    {/* Right Arrow */}
                    {totalSocialPages > 1 && socialPostsPageIndex + 1 < totalSocialPages && (
                      <Box
                        onClick={handleNextSocialPosts}
                        sx={{
                          position: "absolute",
                          right: -20,
                          top: "50%",
                          transform: "translateY(-50%)",
                          width: 36,
                          height: 36,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "50%",
                          background: "#1e293b",
                          color: "#e5e7eb",
                          cursor: "pointer",
                          zIndex: 10,
                          transition: "all 0.2s",
                          border: `1px solid ${cardBorder}`,
                          "&:hover": {
                            background: "#3b82f6",
                            color: "#fff",
                            transform: "translateY(-50%) scale(1.05)",
                          },
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                      </Box>
                    )}

                    <Grid container spacing={2} mb={4} sx={{ px: totalSocialPages > 1 ? 2 : 0 }}>
                      {(visibleSocialPosts.length > 0 ? visibleSocialPosts : DUMMY_SOCIAL_POSTS).map((post: any, i: number) => (
                        <Grid item xs={6} sm={3} key={post.id || i} sx={{ display: "flex" }}>
                          <Paper
                            sx={{
                              p: 1.5,
                              borderRadius: "12px",
                              background: cardBg,
                              border: `1px solid ${cardBorder}`,
                              width: "100%",
                              display: "flex",
                              flexDirection: "column",
                              fontFamily: "Inter, sans-serif",
                              transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                              "&:hover": post.content ? {
                                boxShadow: "0 4px 20px rgba(59,130,246,0.15)",
                                borderColor: "#3b82f6",
                              } : {},
                            }}
                          >
                            <Box
                              sx={{ position: "relative", width: "100%", height: 140, borderRadius: "10px", overflow: "hidden", flexShrink: 0, bgcolor: "#1e293b", cursor: "pointer" }}
                              onClick={() => post.content && setSelectedSocialPost(post)}
                            >
                              {/* <Checkbox
                                checked={selectedSocialPosts.includes(post.id)}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  const checked = e.target.checked;

                                  setSelectedSocialPosts((prev) =>
                                    checked
                                      ? [...prev, post.id]
                                      : prev.filter((id) => id !== post.id)
                                  );
                                }}
                                sx={{
                                  position: "absolute",
                                  top: 4,
                                  left: 4,
                                  zIndex: 2,
                                  color: "#fff",
                                  background: "rgba(0,0,0,0.4)",
                                  borderRadius: "50%",
                                }}
                              /> */}
                              <Box
                                component="img"
                                src={post.image_url || dummyBlogImg}
                                onError={(e: any) => { e.target.src = dummyBlogImg; }}
                                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                                alt="Social post"
                              />
                              {post.platform && (
                                <Chip
                                  label={post.platform}
                                  size="small"
                                  sx={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    height: 20,
                                    fontSize: 10,
                                    background: getPlatformColor(post.platform),
                                    color: "#fff",
                                    fontFamily: "Inter, sans-serif",
                                  }}
                                />
                              )}
                            </Box>

                            <Typography
                              mt={1.5}
                              fontSize={13}
                              fontWeight={600}
                              color="#e5e7eb"
                              lineHeight={1.4}
                              fontFamily="Inter, sans-serif"
                              sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                flexGrow: 1,
                              }}
                            >
                              {post.content?.substring(0, 100) || "No content available"}
                            </Typography>

                            {post.created_at && (
                              <Typography mt={0.75} fontSize={11} color="#6b7280" fontFamily="Inter, sans-serif">
                                {new Date(post.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                              </Typography>
                            )}

                            <Stack direction="row" spacing={1} mt={1} alignItems="center">
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.5}
                                sx={{ ml: "auto", cursor: "pointer" }}
                                // onClick={() => { localStorage.setItem('activeItem', 'socailpost'); navigate('/codegenie/generated-blogs', { state: { activeItem: 'socailpost' } }); }}
                                onClick={(e: any) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  console.log('view post details res=>', post);
                                  setSelectedSocialPost(post);
                                }}
                              >
                                <Typography fontSize={12} color="#3b82f6" fontWeight={600} fontFamily="Inter, sans-serif">
                                  View Post
                                </Typography>
                                <EastOutlinedIcon sx={{ color: "#3b82f6", fontSize: 16 }} />
                              </Stack>
                            </Stack>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>

                    {/* Page indicator */}
                    {totalSocialPages > 1 && (
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={1} mt={-2} mb={2}>
                        <Typography fontSize={11} color="#6b7280" fontFamily="Inter, sans-serif">
                          Page {socialPostsPageIndex + 1} of {totalSocialPages}
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                )}

                {/* View more / View less — Social Posts */}
                {socialPosts.length > postsPerPage && (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: -1, mb: 2 }}>
                    <Typography
                      fontSize={13}
                      color={primary}
                      fontFamily="Inter, sans-serif"
                      sx={{
                        cursor: "pointer",
                        px: 2,
                        py: 0.75,
                        borderRadius: "8px",
                        border: `1px solid ${primary}22`,
                        background: `${primary}11`,
                        transition: "background 0.2s",
                        "&:hover": { background: `${primary}22` },
                      }}
                      onClick={() => setShowAllSocialPosts((prev) => !prev)}
                    >
                      {showAllSocialPosts ? "View less ↑" : "View more ↓"}
                    </Typography>
                  </Box>
                )}
              </>
            );
          })()}

          {/* ================= IMAGES ================= */}
          {(() => {
            const imagesPerPage = 4;
            const totalImagesPages = Math.ceil(images.length / imagesPerPage);
            const displayedImages = images.slice(
              imagesPageIndex * imagesPerPage,
              (imagesPageIndex + 1) * imagesPerPage
            );
            const visibleImages = showAllImages ? images : displayedImages;

            const handleNextImages = () => {
              if (imagesPageIndex + 1 < totalImagesPages) {
                setImagesPageIndex(prev => prev + 1);
              }
            };

            const handlePrevImages = () => {
              if (imagesPageIndex > 0) {
                setImagesPageIndex(prev => prev - 1);
              }
            };

            const handleImageClick = (imageSrc: string, imageTitle: string) => {
              setSelectedImage({ src: imageSrc, title: imageTitle });
              setOpenImageViewer(true);
            };

            return (
              <>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                  mt={4}
                >
                  <Typography
                    color="#e5e7eb"
                    fontWeight={600}
                    fontSize={16}
                    letterSpacing="-0.01em"
                    fontFamily="Inter, sans-serif"
                  >
                    Images ({images.length})
                  </Typography>

                  {/* <Stack direction="row" spacing={1} alignItems="center">
                    <Checkbox
                      checked={
                        images.length > 0 &&
                        selectedImages.length === images.length
                      }
                      onChange={handleSelectAllImages}
                      sx={{
                        p: 0,
                        color: "#4BA5EB",
                        "&.Mui-checked": {
                          color: "#4BA5EB",
                        },
                      }}
                    />

                    <Typography
                      sx={{
                        color: "#fff",
                        cursor: "pointer",
                      }}
                      onClick={handleSelectAllImages}
                    >
                      Select All
                    </Typography>

                    <Button
                      disabled={!selectedImages.length || unpublishingImages}
                      onClick={handleUnpublishImages}
                      sx={{
                        background: "#ff4d4f",
                        color: "#fff",
                        textTransform: "none",
                        borderRadius: "8px",
                        px: 2,
                        minWidth: 170,
                        "&:hover": {
                          background: "#d9363e",
                        },
                      }}
                    >
                      {unpublishingImages ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        `Unpublish Images (${selectedImages.length})`
                      )}
                    </Button>
                  </Stack> */}
                </Stack>

                {loadingImages ? (
                  <Grid container spacing={2} mb={4}>
                    {Array.from(new Array(4)).map((_, i) => (
                      <Grid item xs={6} sm={3} key={i} sx={{ display: "flex" }}>
                        <Paper sx={{ p: 1.5, borderRadius: "12px", background: cardBg, border: `1px solid ${cardBorder}`, width: "100%" }}>
                          <Skeleton variant="rectangular" height={140} sx={{ borderRadius: "10px" }} />
                          <Skeleton variant="text" height={20} sx={{ mt: 1 }} />
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                ) : images.length === 0 ? (
                  <Paper
                    sx={{
                      p: 3,
                      borderRadius: "12px",
                      background: cardBg,
                      border: `1px solid ${cardBorder}`,
                      mb: 4,
                      textAlign: "center",
                    }}
                  >
                    <Typography fontSize={13} color="#6b7280" fontFamily="Inter, sans-serif">
                      No images available
                    </Typography>
                  </Paper>
                ) : (
                  <Box sx={{ position: "relative" }}>
                    {/* Left Arrow */}
                    {totalImagesPages > 1 && imagesPageIndex > 0 && (
                      <Box
                        onClick={handlePrevImages}
                        sx={{
                          position: "absolute",
                          left: -20,
                          top: "50%",
                          transform: "translateY(-50%)",
                          width: 36,
                          height: 36,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "50%",
                          background: "#1e293b",
                          color: "#e5e7eb",
                          cursor: "pointer",
                          zIndex: 10,
                          transition: "all 0.2s",
                          border: `1px solid ${cardBorder}`,
                          "&:hover": {
                            background: "#3b82f6",
                            color: "#fff",
                            transform: "translateY(-50%) scale(1.05)",
                          },
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="15 18 9 12 15 6"></polyline>
                        </svg>
                      </Box>
                    )}

                    {/* Right Arrow */}
                    {totalImagesPages > 1 && imagesPageIndex + 1 < totalImagesPages && (
                      <Box
                        onClick={handleNextImages}
                        sx={{
                          position: "absolute",
                          right: -20,
                          top: "50%",
                          transform: "translateY(-50%)",
                          width: 36,
                          height: 36,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "50%",
                          background: "#1e293b",
                          color: "#e5e7eb",
                          cursor: "pointer",
                          zIndex: 10,
                          transition: "all 0.2s",
                          border: `1px solid ${cardBorder}`,
                          "&:hover": {
                            background: "#3b82f6",
                            color: "#fff",
                            transform: "translateY(-50%) scale(1.05)",
                          },
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                      </Box>
                    )}

                    <Grid container spacing={2} mb={4} sx={{ px: totalImagesPages > 1 ? 2 : 0 }}>
                      {visibleImages.map((image: any, i: number) => {
                        const imageSrc = image.blog_image_path || image.image_path || dummyBlogImg;
                        const imageTitle = image.blog_title || image.title || "Image";
                        return (
                          <Grid item xs={6} sm={3} key={image.id || i} sx={{ display: "flex" }}>
                            <Paper
                              sx={{
                                p: 1.5,
                                borderRadius: "12px",
                                background: cardBg,
                                border: `1px solid ${cardBorder}`,
                                width: "100%",
                                display: "flex",
                                flexDirection: "column",
                                fontFamily: "Inter, sans-serif",
                                transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                                "&:hover": {
                                  boxShadow: "0 4px 20px rgba(59,130,246,0.15)",
                                  borderColor: "#3b82f6",
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  position: "relative",
                                  width: "100%",
                                  height: 140,
                                  borderRadius: "10px",
                                  overflow: "hidden",
                                  flexShrink: 0,
                                  bgcolor: "#1e293b",
                                  cursor: "pointer",
                                }}
                                onClick={() => handleImageClick(imageSrc, imageTitle)}
                              >
                                {/* <Checkbox
                                  checked={selectedImages.includes(image.id)}
                                  onChange={() => handleImageSelect(image.id)}
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{
                                    position: "absolute",
                                    top: 8,
                                    left: 8,
                                    zIndex: 10,
                                    background: "rgba(0,0,0,0.6)",
                                    borderRadius: "50%",
                                    color: "#fff",
                                    "&.Mui-checked": {
                                      color: "#4BA5EB",
                                    },
                                  }}
                                /> */}
                                <Box
                                  component="img"
                                  src={imageSrc}
                                  onError={(e: any) => { e.target.src = dummyBlogImg; }}
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    display: "block",
                                    transition: "transform 0.3s ease",
                                    "&:hover": { transform: "scale(1.05)" },
                                  }}
                                  alt={imageTitle}
                                />
                              </Box>

                              {imageTitle && imageTitle !== "Image" && (
                                <Typography
                                  mt={1}
                                  fontSize={12}
                                  fontWeight={600}
                                  color="#e5e7eb"
                                  lineHeight={1.4}
                                  fontFamily="Inter, sans-serif"
                                  sx={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  }}
                                >
                                  {imageTitle}
                                </Typography>
                              )}

                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.5}
                                mt="auto"
                                pt={1}
                                sx={{ cursor: "pointer" }}
                                onClick={() => handleImageClick(imageSrc, imageTitle)}
                              >
                                <Typography
                                  fontSize={12}
                                  color="#3b82f6"
                                  fontWeight={600}
                                  fontFamily="Inter, sans-serif"
                                >
                                  View Image
                                </Typography>
                                <EastOutlinedIcon sx={{ color: "#3b82f6", fontSize: 14 }} />
                              </Stack>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>

                    {/* Page indicator */}
                    {totalImagesPages > 1 && (
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={1} mt={-2} mb={2}>
                        <Typography fontSize={11} color="#6b7280" fontFamily="Inter, sans-serif">
                          Page {imagesPageIndex + 1} of {totalImagesPages}
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                )}

                {/* View more / View less — Images */}
                {images.length > imagesPerPage && (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: -1, mb: 2 }}>
                    <Typography
                      fontSize={13}
                      color={primary}
                      fontFamily="Inter, sans-serif"
                      sx={{
                        cursor: "pointer",
                        px: 2,
                        py: 0.75,
                        borderRadius: "8px",
                        border: `1px solid ${primary}22`,
                        background: `${primary}11`,
                        transition: "background 0.2s",
                        "&:hover": { background: `${primary}22` },
                      }}
                      onClick={() => setShowAllImages((prev) => !prev)}
                    >
                      {showAllImages ? "View less ↑" : "View more ↓"}
                    </Typography>
                  </Box>
                )}
              </>
            );
          })()}

        </Box>
      </Box>

      {/* ================= VIDEO MODAL ================= */}
      <Modal
        open={helperPopup}
        onClose={() => setHelperPopup(false)}
        disableEscapeKeyDown
      >
        <Box
          sx={{
            position: "absolute" as "absolute",
            top: "50%",
            left: "50%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            transform: "translate(-50%, -50%)",
            width: isMobile ? "95%" : "70%",
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            pt: 2,
            px: 4,
            pb: 3,
            borderRadius: "15px",
            padding: "3%",
            fontFamily: "Inter, sans-serif",
          }}
        >
          {embedSrc ? (
            <iframe
              width="100%"
              height="500"
              src={embedSrc}
              title="Help Video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : helpervideosrc ? (
            <iframe
              width="100%"
              height="500"
              src={helpervideosrc}
              title="Help Video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : (
            <div
              style={{
                width: "100%",
                height: 500,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                fontFamily: "Inter, sans-serif",
              }}
            >
              <div>
                <p>Invalid video URL or embedding not allowed.</p>
                {helpervideosrc && (
                  <a
                    href={helpervideosrc}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open in YouTube
                  </a>
                )}
              </div>
            </div>
          )}
          <Stack flexDirection="row" sx={{ mt: 3 }}>
            <Button
              onClick={() => setHelperPopup(false)}
              variant="contained"
              sx={{ mr: 2, fontFamily: "Inter, sans-serif" }}
            >
              Close
            </Button>
          </Stack>
        </Box>
      </Modal>

      {/* ================= ENQUIRY MODAL ================= */}
      <Modal open={openEnquiryModal} onClose={() => setOpenEnquiryModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 420,
            bgcolor: "#161b22",
            borderRadius: "14px",
            p: 3,
            border: "1px solid #21262d",
            boxShadow: "0 20px 60px rgba(0,0,0,0.15)"
          }}
        >
          <IconButton
            onClick={() => setOpenEnquiryModal(false)}
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              color: "#9ca3af",
              background: "#0d1117",
              width: 32,
              height: 32,
              "&:hover": {
                background: "#21262d",
                color: "#e5e7eb"
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Typography
            color="#e5e7eb"
            fontWeight={700}
            fontSize={16}
            mb={2}
            fontFamily="Inter, sans-serif"
          >
            Send Message
          </Typography>

          <Stack spacing={2}>
            <input
              name="name"
              placeholder="Your Name"
              value={enquiryData.name}
              onChange={handleInputChange}
              className="form-control"
            />

            <input
              name="email"
              placeholder="Email"
              value={enquiryData.email}
              onChange={handleInputChange}
              className="form-control"
            />

            <input
              name="phone"
              placeholder="Phone"
              value={enquiryData.phone}
              onChange={handleInputChange}
              className="form-control"
            />

            <input
              name="subject"
              placeholder="Subject"
              value={enquiryData.subject}
              onChange={handleInputChange}
              className="form-control"
            />

            <textarea
              name="message"
              placeholder="Message"
              value={enquiryData.message}
              onChange={handleInputChange}
              className="form-control"
              rows={3}
            />

            <Stack direction="row" spacing={1}>
              <Button
                fullWidth
                onClick={handleClearEnquiry}
                sx={{
                  border: "1px solid #d0d0d0",
                  color: "#e5e7eb",
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: "8px",
                  fontFamily: "Inter, sans-serif",
                  "&:hover": { borderColor: "#4b5563", background: "#0d1117" },
                }}
              >
                Clear
              </Button>

              <Button
                fullWidth
                onClick={handleSendEnquiry}
                disabled={sendingEnquiry}
                sx={{
                  background: "#3b82f6",
                  color: "#fff",
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: "8px",
                  fontFamily: "Inter, sans-serif",
                  "&:hover": { background: "#2563eb" },
                  "&:disabled": { opacity: 0.6 },
                }}
              >
                {sendingEnquiry ? "Sending..." : "Send"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>

      {/*  Image Viewer Modal */}
      <Modal
        open={openImageViewer}
        onClose={() => setOpenImageViewer(false)}
        disableEscapeKeyDown
      >
        <Box
          sx={{
            position: "absolute" as "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "auto",
            maxWidth: "90vw",
            maxHeight: "90vh",
            bgcolor: "#161b22",
            borderRadius: "16px",
            border: `1px solid ${cardBorder}`,
            boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
            overflow: "hidden",
            fontFamily: "Inter, sans-serif",
          }}
        >
          <IconButton
            onClick={() => setOpenImageViewer(false)}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              zIndex: 10,
              color: "#e5e7eb",
              background: "rgba(0,0,0,0.6)",
              width: 36,
              height: 36,
              "&:hover": {
                background: "rgba(0,0,0,0.8)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          {selectedImage && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 2,
              }}
            >
              <Box
                component="img"
                src={selectedImage.src}
                alt={selectedImage.title}
                sx={{
                  maxWidth: "calc(90vw - 32px)",
                  maxHeight: "calc(90vh - 100px)",
                  objectFit: "contain",
                  borderRadius: "8px",
                  display: "block",
                }}
                onError={(e: any) => {
                  e.target.src = dummyBlogImg;
                }}
              />
              {selectedImage.title && selectedImage.title !== "Image" && (
                <Typography
                  mt={2}
                  fontSize={14}
                  color="#e5e7eb"
                  fontWeight={500}
                  textAlign="center"
                  fontFamily="Inter, sans-serif"
                  sx={{ wordBreak: "break-word", maxWidth: "80vw" }}
                >
                  {selectedImage.title}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Modal>

      <Modal
        open={!!selectedSocialPost}
      // onClose={() => setSelectedSocialPost(null)}
      >
        {/* <Box
          sx={{
            background: "#15151D",
            border: "1px solid rgba(255,255,255,0.07)",
            borderRadius: "14px",
            overflow: "hidden",
          }}
        > */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 700,
            maxWidth: "95%",
            maxHeight: "90vh",
            overflow: "auto",
            bgcolor: "#15151D",
            borderRadius: "14px",
            outline: "none",
            P: 2
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 2,
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                background:
                  "linear-gradient(90deg, #0168B5 0%, #4BA5EB 100%)",
              }}
            >
              {(userDetails?.first_name || location.pathname?.split("/profile/")[1]?.split("-")[0] || "U")
                ?.split(" ")
                ?.map((x: string) => x[0])
                ?.join("")
                ?.substring(0, 2)}
            </Avatar>

            <Box flex={1} display="flex"
              flexDirection="column"
              sx={{ gap: 0.2 }}>
              <Typography fontWeight={700} color="rgb(232, 234, 240)" fontSize={"13px"}>
                {userDetails?.first_name || location.pathname || "User"}
              </Typography>

              <Typography
                variant="caption"
                sx={{ color: "rgba(255, 255, 255, 0.45)", fontSize: '11px' }}
              >
                Just now • 🌐
              </Typography>
              <IconButton
                onClick={() => setSelectedSocialPost(null)}
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  color: "rgba(255,255,255,0.7)",
                  zIndex: 10,
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Title */}
          {previewTitle && (
            <Typography
              sx={{
                px: 2,
                pb: 1,
                color: "rgb(232, 234, 240)",
                fontWeight: 700,
                fontSize: "15px",
                textAlign: 'left'
              }}
            >
              {previewTitle}
            </Typography>
          )}

          {/* Content */}
          <Typography
            sx={{
              px: 2,
              pb: 2,
              color: "rgba(255, 255, 255, 0.65)",
              lineHeight: 1.8,
              whiteSpace: "pre-wrap",
              textAlign: 'left',
              fontSize: '13px'
            }}
          >
            {previewContent}
          </Typography>

          {/* Image */}
          {previewImage && (
            <Box
              component="img"
              src={previewImage}
              sx={{
                width: "100%",
                maxHeight: 420,
                objectFit: "cover",
              }}
            />
          )}
        </Box>
      </Modal>

    </>
  );
}